import React, { useState, useEffect, useRef, useCallback } from "react";
import Map, { Marker, NavigationControl } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { useParams, useNavigate } from "react-router-dom";
import { createToast } from "../../hooks/useToast";
import { API_URL } from "../../config";

// ==================== ANIMATED COLLAPSE ====================
// Smooth height-animate any conditional block.
const Collapse = ({ show, children }) => {
  const ref = useRef(null);
  // rendered tracks whether we keep the DOM subtree alive
  // We keep it alive during the closing animation, then destroy
  const [rendered, setRendered] = useState(show);
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);

    if (show) {
      setRendered(true);
    } else {
      // Start close animation, then unmount
      timerRef.current = setTimeout(() => setRendered(false), 380);
    }
    return () => clearTimeout(timerRef.current);
  }, [show]);

  return (
    <div
      ref={ref}
      style={{
        overflow: 'hidden',
        maxHeight: show ? '2000px' : '0',
        opacity: show ? 1 : 0,
        transform: show ? 'translateY(0)' : 'translateY(-6px)',
        transition: 'max-height 0.38s cubic-bezier(0.4,0,0.2,1), opacity 0.28s ease, transform 0.28s ease',
        pointerEvents: show ? 'auto' : 'none',
      }}
    >
      {rendered && children}
    </div>
  );
};

// ==================== ANIM SECTION (scroll reveal) ====================
const AnimSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: vis ? 1 : 0,
        transform: vis ? 'translateY(0)' : 'translateY(22px)',
        transition: `opacity 0.52s ease ${delay}ms, transform 0.52s cubic-bezier(0.4,0,0.2,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// ==================== ПЕРЕВОДЫ ====================
const translations = {
  en: {
    title: "Create Event",
    updateEvent: "Update Event",
    subtitle: "Fill in the details below to create your event",
    basicInfo: "Basic Information",
    eventName: "Event Name",
    eventNamePlaceholder: "e.g., Tech Conference 2024",
    date: "Date",
    time: "Time",
    location: "Location",
    locationPlaceholder: "Search address or drag marker on map",
    eventCover: "Event Cover",
    dropImage: "Click to upload cover image",
    invalidImageType: "Invalid image type. Please upload PNG, JPG, or WEBP.",
    fileTooLarge: "File too large. Maximum size is 5MB.",
    coverUploaded: "Cover uploaded successfully!",
    cropTitle: "Crop cover image",
    cropHint: "Drag the frame to position. Drag a handle to resize.",
    cropApply: "Apply crop",
    recropCover: "Re-crop",
    description: "Description",
    descriptionPlaceholder: "Tell attendees about your event...",
    organizer: "Organizer Information",
    organizerName: "Organizer Name",
    organizerNamePlaceholder: "Your name or company",
    email: "Email",
    emailPlaceholder: "contact@example.com",
    phone: "Phone",
    phonePlaceholder: "6 12 34 56 78",
    tickets: "Tickets & Pricing",
    isFree: "Free Event",
    ticketPrice: "Ticket Price",
    currency: "Currency",
    visibility: "Visibility & Privacy",
    isPrivate: "Private",
    isPrivateDesc: "Only invited guests can see and join",
    isPublic: "Public",
    isPublicDesc: "Anyone can discover and join",
    invitations: "Invitations",
    allowUserInvitations: "Allow Guest Invitations",
    allowUserInvitationsDesc: "Let attendees invite their friends",
    matching: "AI Matching",
    allowExternalMatching: "Enable AI Matching",
    allowExternalMatchingDesc: "Connect attendees with similar interests before the event",
    matchingStartBefore: "Matching on the event begins from this date",
    matchingStartAfter: "End of the unlimited matching period for this event",
    matchingCategories: "Matching Categories",
    matchingCategoriesRequired: "Please select at least one matching category",
    business: "Business",
    startups: "Startups",
    job: "Job Dating",
    clients: "Finding Clients",
    participantSettings: "Participant Settings",
    requireParticipantPhone: "Require Phone Verification",
    requireParticipantPhoneDesc: "Ensure real attendees by verifying phone numbers",
    capacity: "Max Capacity",
    capacityPlaceholder: "e.g. 200",
    capacityDesc: "Maximum number of participants",
    capacitySection: "Capacity & Cost",
    capacitySectionDesc: "Set the maximum number of participants and see estimated platform cost.",
    estimatedCost: "Estimated platform cost",
    estimatedCostNote: "€2 per participant",
    cancel: "Cancel",
    loading: "Creating...",
    updating: "Updating...",
    searching: "Searching...",
    noResults: "No results found",
    createEvent: "Create Event",
    update: "Update",
    requiredField: "This field is required",
    invalidEmail: "Invalid email format",
    invalidPhone: "Invalid phone number",
    eventCreated: "Event created successfully!",
    eventUpdated: "Event updated successfully!",
    errorCreatingEvent: "Error creating event. Please try again.",
    errorUpdatingEvent: "Error updating event. Please try again.",
    coverRequired: "Please upload an event cover image",
    addressRequired: "Please select a location from suggestions or map",
    changeCover: "Change photo",
    eventSubmitted: "Your event request has been submitted, please wait for the administrator to approve it!",
    invalidTicketPrice: "Invalid ticket price format",
    ticketPriceTooHigh: "Max ticket price: 9,999,999,999.99 (database limit)",
    ticketPriceRequired: "Ticket price is required for paid events",
    requiredFieldsNote: "* Required fields",
    cantCreateYet: "Can't create yet — please complete:",
    issueTitle: "Event name",
    issueDescription: "Description",
    issueDate: "Event date",
    issueLocation: "Address (select from suggestions or pin on map)",
    issueCover: "Cover image",
    issueTicketPrice: "Ticket price",
    issueTablesConfig: "Tables configuration (≥1 table, ≥2 seats)",

    // Block 0 — first event is free
    firstEventBadge: "🎉 Your first event is free!",
    firstEventTitle: "Welcome, new organizer — your first event costs you nothing.",
    firstEventDesc: "As a first-time organizer on our platform, you are not charged any platform fee for your very first event. Participants join for free and you pay nothing. Platform fees (€2 per participant) will only apply from your second event onwards. Make the most of it — create an event you're proud of!",
    firstEventCapacityNote: "First event · no platform fee",

    // Block 6.1 — pricing explanation
    pricingExplainTitle: "How does pricing work?",
    pricingExplainFreeTitle: "🆓 Free Event — organizer covers the platform fee",
    pricingExplainFreeDesc: "Choosing a free event means your attendees pay nothing to join. However, as the organizer you will be billed €2 for every participant who registers. This fee covers the platform's services: AI matching, notifications, participant management and support. Example: 50 participants → you pay €100 in total.",
    pricingExplainPaidTitle: "💳 Paid Event — participants cover everything",
    pricingExplainPaidDesc: "Choosing a paid event means each attendee pays for their own spot. The base platform fee of €2 per participant is automatically included in the ticket price, and you freely set any additional amount on top as your revenue. Example: you set €10 per ticket → the attendee pays €10, €2 goes to the platform, €8 goes to you.",

    // Block 10.1 — tables
    tables: "Tables & Seating",
    tablesDesc: "Enable physical tables so matched participants can easily find each other on the day of the event.",
    hasTables: "Enable Tables",
    hasTablesDesc: "After matching, each participant will receive a table number. They simply walk to that table to meet their match in person.",
    numberOfTables: "Number of Tables",
    numberOfTablesPlaceholder: "e.g. 10",
    seatsPerTable: "Seats per Table",
    seatsPerTablePlaceholder: "e.g. 4",
    seatsPerTableNote: "min. 2 seats",
    totalSeatsLabel: "Total seats",
    tablesExplain: "How it works: when two participants are matched by AI, they are assigned the same table number. On the event day, they find each other by going to that table — no awkward searching required.",
    tablesRequired: "Number of tables and seats per table are required when tables are enabled (min 1 table, min 2 seats)",

    // Block 10.2 — table timers
    tableTimers: "Table Timers",
    tableTimersDesc: "Set timers to manage the pace of your networking sessions at each table.",
    tableTimerSeatLabel: "Time at table",
    tableTimerSeatDesc: "How long each pair spends talking at a table before the bell rings",
    tableTimerRotationLabel: "Table rotation",
    tableTimerRotationDesc: "Cooldown time between each table switch",
    timerMinutes: "min",

    // Drafts
    saveDraft: "Save as Draft",
    savingDraft: "Saving...",
    draftSaved: "Draft saved! You can continue editing later.",
    draftUpdated: "Draft updated!",
    draftError: "Error saving draft. Please try again.",
    continueDraft: "Continue Editing",
    draftBadge: "Draft",
    draftAutoSaveHint: "Your progress is saved automatically as a draft",
    autoSaving: "Auto-saving...",
    autoSaved: "Draft auto-saved",
    autoSaveReady: "Fill in name, description & address to auto-save",

    reviewTitle: "Review & Submit",
    reviewHint: "Double-check the details above before submitting.",
    preview: "Live Preview",
    previewHint: "Updates as you type",
    free: "Free",
    private: "Private",
    public: "Public",
    organizedBy: "Organized by",
  },
  fr: {
    title: "Créer un Événement",
    updateEvent: "Mettre à Jour l'Événement",
    subtitle: "Remplissez les détails ci-dessous pour créer votre événement",
    basicInfo: "Informations de Base",
    eventName: "Nom de l'Événement",
    eventNamePlaceholder: "ex: Conférence Tech 2024",
    date: "Date",
    time: "Heure",
    location: "Lieu",
    locationPlaceholder: "Rechercher une adresse ou glisser le marqueur sur la carte",
    eventCover: "Couverture de l'Événement",
    dropImage: "Cliquez pour télécharger l'image",
    invalidImageType: "Type d'image invalide. Veuillez télécharger PNG, JPG ou WEBP.",
    fileTooLarge: "Fichier trop volumineux. Taille maximale 5 Mo.",
    coverUploaded: "Couverture téléchargée avec succès !",
    cropTitle: "Recadrer la couverture",
    cropHint: "Glissez le cadre pour positionner. Tirez une poignée pour redimensionner.",
    cropApply: "Appliquer",
    recropCover: "Recadrer",
    description: "Description",
    descriptionPlaceholder: "Décrivez votre événement aux participants...",
    organizer: "Informations sur l'Organisateur",
    organizerName: "Nom de l'Organisateur",
    organizerNamePlaceholder: "Votre nom ou entreprise",
    email: "Email",
    emailPlaceholder: "contact@exemple.com",
    phone: "Téléphone",
    phonePlaceholder: "6 12 34 56 78",
    tickets: "Billets & Prix",
    isFree: "Événement Gratuit",
    ticketPrice: "Prix du Billet",
    currency: "Devise",
    visibility: "Visibilité & Confidentialité",
    isPrivate: "Privé",
    isPrivateDesc: "Seuls les invités peuvent voir et rejoindre",
    isPublic: "Public",
    isPublicDesc: "Tout le monde peut découvrir et rejoindre",
    invitations: "Invitations",
    allowUserInvitations: "Autoriser les Invitations d'Invités",
    allowUserInvitationsDesc: "Permettre aux participants d'inviter leurs amis",
    matching: "Matching IA",
    allowExternalMatching: "Activer le Matching IA",
    allowExternalMatchingDesc: "Connecter les participants ayant des intérêts similaires avant l'événement",
    matchingStartBefore: "La mise en relation pour l'événement commence à partir de cette date.",
    matchingStartAfter: "Fin de la période de mise en relation illimitée pour cet événement",
    matchingCategories: "Catégories de Matching",
    matchingCategoriesRequired: "Veuillez sélectionner au moins une catégorie de matching",
    business: "Affaires",
    startups: "Startups",
    job: "Job Dating",
    clients: "Trouver des Clients",
    participantSettings: "Paramètres des Participants",
    requireParticipantPhone: "Exiger la Vérification Téléphonique",
    requireParticipantPhoneDesc: "Assurer des participants réels en vérifiant les numéros de téléphone",
    capacity: "Capacité maximale",
    capacityPlaceholder: "ex. 200",
    capacityDesc: "Nombre maximum de participants",
    capacitySection: "Capacité & Coût",
    capacitySectionDesc: "Définissez le nombre maximum de participants et visualisez le coût estimé.",
    estimatedCost: "Coût plateforme estimé",
    estimatedCostNote: "2€ par participant",
    cancel: "Annuler",
    loading: "Création...",
    updating: "Mise à jour...",
    searching: "Recherche...",
    noResults: "Aucun résultat trouvé",
    createEvent: "Créer l'Événement",
    update: "Mettre à Jour",
    requiredField: "Ce champ est requis",
    invalidEmail: "Format email invalide",
    invalidPhone: "Numéro de téléphone invalide",
    eventCreated: "Événement créé avec succès !",
    eventUpdated: "Événement mis à jour avec succès !",
    errorCreatingEvent: "Erreur lors de la création de l'événement. Veuillez réessayer.",
    errorUpdatingEvent: "Erreur lors de la mise à jour de l'événement. Veuillez réessayer.",
    coverRequired: "Veuillez télécharger une image de couverture",
    addressRequired: "Veuillez sélectionner un lieu dans les suggestions ou sur la carte",
    changeCover: "Changer la photo",
    eventSubmitted: "Votre demande d'événement a été soumise, veuillez attendre l'approbation de l'administrateur !",
    invalidTicketPrice: "Format de prix du billet invalide",
    ticketPriceTooHigh: "Prix maximum du billet : 9 999 999 999,99 (limite de la base de données)",
    ticketPriceRequired: "Le prix du billet est requis pour les événements payants",
    requiredFieldsNote: "* Champs requis",
    cantCreateYet: "Impossible de créer — à compléter :",
    issueTitle: "Nom de l'événement",
    issueDescription: "Description",
    issueDate: "Date de l'événement",
    issueLocation: "Adresse (choisir dans la liste ou placer sur la carte)",
    issueCover: "Image de couverture",
    issueTicketPrice: "Prix du billet",
    issueTablesConfig: "Configuration des tables (≥1 table, ≥2 places)",

    // Block 0 — premier événement gratuit
    firstEventBadge: "🎉 Votre premier événement est gratuit !",
    firstEventTitle: "Bienvenue, nouvel organisateur — votre premier événement ne vous coûte rien.",
    firstEventDesc: "En tant que nouvel organisateur sur notre plateforme, aucun frais de plateforme ne vous sera facturé pour votre tout premier événement. Les participants rejoignent gratuitement et vous ne payez rien. Les frais de plateforme (2 € par participant) ne s'appliqueront qu'à partir de votre deuxième événement. Profitez-en — créez un événement dont vous serez fier !",
    firstEventCapacityNote: "Premier événement · aucun frais de plateforme",

    // Block 6.1 — explication du modèle tarifaire
    pricingExplainTitle: "Comment fonctionne le tarif ?",
    pricingExplainFreeTitle: "🆓 Événement Gratuit — l'organisateur couvre les frais",
    pricingExplainFreeDesc: "Choisir un événement gratuit signifie que vos participants ne paient rien pour s'inscrire. En revanche, en tant qu'organisateur, vous serez facturé 2 € pour chaque participant inscrit. Ces frais couvrent les services de la plateforme : mise en relation IA, notifications, gestion des participants et support. Exemple : 50 participants → vous payez 100 € au total.",
    pricingExplainPaidTitle: "💳 Événement Payant — les participants paient pour eux-mêmes",
    pricingExplainPaidDesc: "Choisir un événement payant signifie que chaque participant règle sa propre place. Les frais de plateforme de 2 € par participant sont automatiquement inclus dans le prix du billet, et vous définissez librement le montant supplémentaire qui vous revient. Exemple : vous fixez le prix à 10 € par billet → le participant paie 10 €, 2 € vont à la plateforme, 8 € vous reviennent.",

    // Block 10.1 — tables
    tables: "Tables & Places Assises",
    tablesDesc: "Activez les tables physiques pour que les participants matchés se retrouvent facilement le jour de l'événement.",
    hasTables: "Activer les Tables",
    hasTablesDesc: "Après le matching, chaque participant reçoit un numéro de table. Il se rend simplement à cette table pour rencontrer son match en personne.",
    numberOfTables: "Nombre de Tables",
    numberOfTablesPlaceholder: "ex. 10",
    seatsPerTable: "Places par Table",
    seatsPerTablePlaceholder: "ex. 4",
    seatsPerTableNote: "min. 2 places",
    totalSeatsLabel: "Places totales",
    tablesExplain: "Comment ça fonctionne : lorsque deux participants sont matchés par l'IA, ils reçoivent le même numéro de table. Le jour de l'événement, ils se retrouvent en allant à leur table — sans recherche gênante.",
    tablesRequired: "Le nombre de tables et de places est requis lorsque les tables sont activées (min 1 table, min 2 places)",

    // Block 10.2 — minuteries de table
    tableTimers: "Minuteries",
    tableTimersDesc: "Configurez les minuteries pour cadencer les échanges à chaque table.",
    tableTimerSeatLabel: "Temps à table",
    tableTimerSeatDesc: "Durée de chaque échange entre deux participants avant le signal",
    tableTimerRotationLabel: "Rotation de tables",
    tableTimerRotationDesc: "Temps de transition entre chaque changement de table",
    timerMinutes: "min",

    // Drafts
    saveDraft: "Sauvegarder le brouillon",
    savingDraft: "Sauvegarde...",
    draftSaved: "Brouillon sauvegardé ! Vous pourrez continuer plus tard.",
    draftUpdated: "Brouillon mis à jour !",
    draftError: "Erreur lors de la sauvegarde du brouillon. Veuillez réessayer.",
    continueDraft: "Continuer l'édition",
    draftBadge: "Brouillon",
    draftAutoSaveHint: "Votre progression est sauvegardée automatiquement",
    autoSaving: "Sauvegarde auto...",
    autoSaved: "Brouillon sauvegardé",
    autoSaveReady: "Remplissez le nom, la description et l'adresse pour sauvegarder",

    reviewTitle: "Vérifier et Soumettre",
    reviewHint: "Vérifiez les détails ci-dessus avant de soumettre.",
    preview: "Aperçu en direct",
    previewHint: "Se met à jour en temps réel",
    free: "Gratuit",
    private: "Privé",
    public: "Public",
    organizedBy: "Organisé par",
  }
};

// ==================== LIVE PREVIEW CARD ====================
const EventPreviewCard = ({ formData, coverPreview, themeVars, t, lang }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'short', month: 'short', day: 'numeric'
    });
  };

  const priceLabel = formData.isFree
    ? t.free
    : formData.ticketPrice
      ? `${formData.ticketPrice} ${formData.currency}`
      : null;

  return (
    <div className="prev-card">
      {/* Cover */}
      <div className="prev-cover">
        {coverPreview ? (
          <img src={coverPreview} alt="" className="prev-cover-img" />
        ) : (
          <div className="prev-cover-empty">
            <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" opacity="0.25">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21zm16.5-12a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
            </svg>
          </div>
        )}
        <div className="prev-badges">
          <span className="prev-badge-vis">{formData.isPrivate ? t.private : t.public}</span>
          {priceLabel && <span className="prev-badge-price">{priceLabel}</span>}
        </div>
      </div>

      {/* Body */}
      <div className="prev-body">
        <div className="prev-title">
          {formData.title || <span className="prev-placeholder">Event name...</span>}
        </div>

        {(formData.date || formData.time) && (
          <div className="prev-meta">
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="prev-icon">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            {[formatDate(formData.date), formData.time].filter(Boolean).join(' · ')}
          </div>
        )}

        {formData.location && (
          <div className="prev-meta">
            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="prev-icon">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            <span className="prev-loc">{formData.location}</span>
          </div>
        )}

        {formData.description && (
          <p className="prev-desc">
            {formData.description.slice(0, 110)}{formData.description.length > 110 ? '…' : ''}
          </p>
        )}

        {formData.organizer && (
          <div className="prev-org">
            <div className="prev-org-av">{formData.organizer.charAt(0).toUpperCase()}</div>
            <div>
              <div className="prev-org-lbl">{t.organizedBy}</div>
              <div className="prev-org-name">{formData.organizer}</div>
            </div>
          </div>
        )}

        {formData.allowExternalMatching && (
          <div className="prev-ai-tag">
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
            </svg>
            AI Matching
          </div>
        )}

        {formData.hasTables && formData.numberOfTables >= 1 && formData.seatsPerTable >= 2 && (
          <div className="prev-ai-tag" style={{ background: "rgba(251,191,36,0.12)", color: "#f59e0b", borderColor: "rgba(251,191,36,0.25)" }}>
            <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <rect x="3" y="8" width="18" height="8" rx="1"/><line x1="8" y1="8" x2="8" y2="16"/><line x1="16" y1="8" x2="16" y2="16"/><line x1="3" y1="12" x2="21" y2="12"/>
            </svg>
            {lang === "fr"
              ? `${formData.numberOfTables} tables · ${formData.seatsPerTable} places`
              : `${formData.numberOfTables} tables · ${formData.seatsPerTable} seats`}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== IMAGE CROP MODAL ====================
const ASPECT_RATIOS = [
  { key: '16:9', label: '16:9', value: 16 / 9 },
  { key: '4:3',  label: '4:3',  value: 4 / 3 },
  { key: '1:1',  label: '1:1',  value: 1 },
  { key: 'free', label: '↔',   value: null },
];

const ImageCropModal = ({ src, fileName, fileType, onCancel, onApply, isLight, t }) => {
  const imgRef = useRef(null);
  const stageRef = useRef(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [crop, setCrop] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [aspectKey, setAspectKey] = useState('16:9');
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);

  const aspect = ASPECT_RATIOS.find(a => a.key === aspectKey)?.value ?? null;

  const fitCrop = useCallback((dispW, dispH, aspectVal) => {
    if (aspectVal == null) {
      const w = dispW * 0.8, h = dispH * 0.8;
      return { x: (dispW - w) / 2, y: (dispH - h) / 2, w, h };
    }
    let w, h;
    if (dispW / dispH > aspectVal) { h = dispH; w = h * aspectVal; }
    else { w = dispW; h = w / aspectVal; }
    return { x: (dispW - w) / 2, y: (dispH - h) / 2, w, h };
  }, []);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const onload = () => {
      const maxW = Math.min(720, window.innerWidth - 80);
      const maxH = Math.min(460, window.innerHeight - 260);
      const nw = img.naturalWidth, nh = img.naturalHeight;
      const ratio = Math.min(maxW / nw, maxH / nh, 1);
      const dw = Math.round(nw * ratio), dh = Math.round(nh * ratio);
      setImgSize({ w: dw, h: dh });
      setCrop(fitCrop(dw, dh, aspect));
    };
    if (img.complete && img.naturalWidth) onload();
    else img.addEventListener('load', onload);
    return () => img.removeEventListener('load', onload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // Re-fit crop when aspect changes
  useEffect(() => {
    if (imgSize.w && imgSize.h) setCrop(fitCrop(imgSize.w, imgSize.h, aspect));
  }, [aspectKey, imgSize.w, imgSize.h, fitCrop, aspect]);

  const startDrag = (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    const point = e.touches ? e.touches[0] : e;
    setDrag({ type, startX: point.clientX, startY: point.clientY, startCrop: { ...crop } });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (ev) => {
      const point = ev.touches ? ev.touches[0] : ev;
      const dx = point.clientX - drag.startX;
      const dy = point.clientY - drag.startY;
      const sc = drag.startCrop;

      if (drag.type === 'move') {
        let nx = Math.max(0, Math.min(imgSize.w - sc.w, sc.x + dx));
        let ny = Math.max(0, Math.min(imgSize.h - sc.h, sc.y + dy));
        setCrop({ ...sc, x: nx, y: ny });
        return;
      }

      // Resize handles. Each corner anchors the opposite corner.
      const minSize = 40;
      let nx = sc.x, ny = sc.y, nw = sc.w, nh = sc.h;
      const right = sc.x + sc.w, bottom = sc.y + sc.h;

      const horiz = drag.type.includes('e') ? 'e' : drag.type.includes('w') ? 'w' : null;
      const vert  = drag.type.includes('s') ? 's' : drag.type.includes('n') ? 'n' : null;

      if (horiz === 'e') nw = Math.max(minSize, sc.w + dx);
      if (horiz === 'w') { nw = Math.max(minSize, sc.w - dx); nx = right - nw; }
      if (vert  === 's') nh = Math.max(minSize, sc.h + dy);
      if (vert  === 'n') { nh = Math.max(minSize, sc.h - dy); ny = bottom - nh; }

      if (aspect != null) {
        // Drive secondary dimension from the dragged one (prefer horizontal axis)
        if (horiz) { nh = nw / aspect; if (vert === 'n') ny = bottom - nh; }
        else if (vert) { nw = nh * aspect; if (horiz === 'w') nx = right - nw; }
      }

      // Clamp inside image
      if (nx < 0) { nw += nx; nx = 0; if (aspect != null) nh = nw / aspect; }
      if (ny < 0) { nh += ny; ny = 0; if (aspect != null) nw = nh * aspect; }
      if (nx + nw > imgSize.w) { nw = imgSize.w - nx; if (aspect != null) nh = nw / aspect; }
      if (ny + nh > imgSize.h) { nh = imgSize.h - ny; if (aspect != null) nw = nh * aspect; }

      setCrop({ x: nx, y: ny, w: nw, h: nh });
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [drag, imgSize, aspect]);

  const handleApply = async () => {
    const img = imgRef.current;
    if (!img || !imgSize.w || !imgSize.h || crop.w < 4 || crop.h < 4) return;
    setBusy(true);
    try {
      const sx = (crop.x / imgSize.w) * img.naturalWidth;
      const sy = (crop.y / imgSize.h) * img.naturalHeight;
      const sw = (crop.w / imgSize.w) * img.naturalWidth;
      const sh = (crop.h / imgSize.h) * img.naturalHeight;

      const maxOut = 1920;
      const scale = Math.min(1, maxOut / sw);
      const outW = Math.max(1, Math.round(sw * scale));
      const outH = Math.max(1, Math.round(sh * scale));

      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

      const mime = fileType === 'image/png' ? 'image/png' : 'image/jpeg';
      const ext = mime === 'image/png' ? 'png' : 'jpg';
      const baseName = (fileName || 'cover').replace(/\.[^.]+$/, '');
      canvas.toBlob((blob) => {
        setBusy(false);
        if (!blob) return;
        const file = new File([blob], `${baseName}-cropped.${ext}`, { type: mime });
        onApply(file);
      }, mime, 0.92);
    } catch (e) {
      setBusy(false);
    }
  };

  const overlayBg = isLight ? 'rgba(15, 23, 42, 0.55)' : 'rgba(2, 6, 23, 0.7)';
  const cardBg    = isLight ? '#ffffff' : '#152035';
  const cardFg    = isLight ? '#1e293b' : '#e2e8f0';
  const muted     = isLight ? '#64748b' : '#94a3b8';
  const border    = isLight ? 'rgba(99,102,241,0.18)' : 'rgba(148,163,252,0.13)';
  const primary   = '#6366f1';
  const accent    = '#8b5cf6';

  const handle = (cur, pos) => {
    const sz = 14;
    const styles = {
      position: 'absolute', width: sz, height: sz,
      background: '#fff', border: `2px solid ${primary}`, borderRadius: 3,
      cursor: cur, touchAction: 'none', zIndex: 3,
    };
    const half = -sz / 2;
    if (pos.includes('n')) styles.top = half; else if (pos.includes('s')) styles.bottom = half;
    if (pos.includes('w')) styles.left = half; else if (pos.includes('e')) styles.right = half;
    if (pos === 'n' || pos === 's') { styles.left = '50%'; styles.transform = 'translateX(-50%)'; }
    if (pos === 'e' || pos === 'w') { styles.top  = '50%'; styles.transform = 'translateY(-50%)'; }
    return (
      <div
        style={styles}
        onMouseDown={(e) => startDrag(e, pos)}
        onTouchStart={(e) => startDrag(e, pos)}
      />
    );
  };

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, background: overlayBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        animation: 'ce-fade-in 0.18s ease',
      }}
    >
      <div style={{
        background: cardBg, color: cardFg, borderRadius: 14,
        boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        padding: 20, maxWidth: 'calc(100vw - 32px)',
        border: `1px solid ${border}`,
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '1rem', fontWeight: 700 }}>{t.cropTitle}</div>
            <div style={{ fontSize: '0.74rem', color: muted, marginTop: 2 }}>{t.cropHint}</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {ASPECT_RATIOS.map(r => (
              <button key={r.key} type="button" onClick={() => setAspectKey(r.key)}
                style={{
                  padding: '5px 10px', borderRadius: 6, fontSize: '0.74rem',
                  fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  border: `1px solid ${aspectKey === r.key ? primary : border}`,
                  background: aspectKey === r.key ? `${primary}1a` : 'transparent',
                  color: aspectKey === r.key ? primary : muted,
                  transition: 'all 0.15s',
                }}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <div ref={stageRef} style={{
          position: 'relative', width: imgSize.w || 720, height: imgSize.h || 460,
          background: '#000', borderRadius: 8, overflow: 'hidden',
          margin: '0 auto', userSelect: 'none', touchAction: 'none',
        }}>
          <img ref={imgRef} src={src} alt="" draggable={false}
            crossOrigin="anonymous"
            style={{ display: 'block', width: imgSize.w || 'auto', height: imgSize.h || 'auto', pointerEvents: 'none' }} />
          {imgSize.w > 0 && (
            <>
              {/* Dim outside */}
              <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                boxShadow: `0 0 0 9999px rgba(0,0,0,0.55) inset`,
                clipPath: `polygon(
                  0 0, 100% 0, 100% 100%, 0 100%, 0 0,
                  ${crop.x}px ${crop.y}px,
                  ${crop.x}px ${crop.y + crop.h}px,
                  ${crop.x + crop.w}px ${crop.y + crop.h}px,
                  ${crop.x + crop.w}px ${crop.y}px,
                  ${crop.x}px ${crop.y}px
                )`,
              }} />
              {/* Crop frame */}
              <div
                onMouseDown={(e) => startDrag(e, 'move')}
                onTouchStart={(e) => startDrag(e, 'move')}
                style={{
                  position: 'absolute',
                  left: crop.x, top: crop.y, width: crop.w, height: crop.h,
                  border: `2px solid ${primary}`, cursor: 'move',
                  boxShadow: `0 0 0 1px rgba(255,255,255,0.2)`,
                  touchAction: 'none', zIndex: 2,
                }}
              >
                {/* Rule-of-thirds grid */}
                <div style={{ position:'absolute', inset:0, pointerEvents:'none',
                  backgroundImage:
                    `linear-gradient(to right, transparent 33.33%, rgba(255,255,255,0.35) 33.33%, rgba(255,255,255,0.35) calc(33.33% + 1px), transparent calc(33.33% + 1px), transparent 66.66%, rgba(255,255,255,0.35) 66.66%, rgba(255,255,255,0.35) calc(66.66% + 1px), transparent calc(66.66% + 1px)),
                     linear-gradient(to bottom, transparent 33.33%, rgba(255,255,255,0.35) 33.33%, rgba(255,255,255,0.35) calc(33.33% + 1px), transparent calc(33.33% + 1px), transparent 66.66%, rgba(255,255,255,0.35) 66.66%, rgba(255,255,255,0.35) calc(66.66% + 1px), transparent calc(66.66% + 1px))`,
                }} />
                {handle('nwse-resize', 'nw')}
                {handle('nesw-resize', 'ne')}
                {handle('nesw-resize', 'sw')}
                {handle('nwse-resize', 'se')}
                {handle('ns-resize', 'n')}
                {handle('ns-resize', 's')}
                {handle('ew-resize', 'e')}
                {handle('ew-resize', 'w')}
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: '0.72rem', color: muted }}>
            {imgSize.w > 0 && `${Math.round((crop.w / imgSize.w) * (imgRef.current?.naturalWidth || 0))} × ${Math.round((crop.h / imgSize.h) * (imgRef.current?.naturalHeight || 0))} px`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onCancel} disabled={busy}
              style={{
                padding: '8px 18px', borderRadius: 7, fontSize: '0.85rem', fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
                background: 'transparent', color: muted, border: `1px solid ${border}`,
              }}>
              {t.cancel}
            </button>
            <button type="button" onClick={handleApply} disabled={busy}
              style={{
                padding: '8px 22px', borderRadius: 7, fontSize: '0.85rem', fontWeight: 600,
                cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                background: `linear-gradient(135deg, ${primary}, ${accent})`,
                color: '#fff', border: 'none',
                opacity: busy ? 0.6 : 1,
                boxShadow: `0 3px 12px ${primary}30`,
              }}>
              {busy ? '…' : t.cropApply}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== ОСНОВНОЙ КОМПОНЕНТ ====================
export default function CreateEvents({ lang = "en", theme = "dark", token, isGuruDomain = false }) {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const toast = (message, type = "info", duration = 5000) => createToast(message, type, duration);

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverError, setCoverError] = useState("");
  const [position, setPosition] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const locationInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const mapRef = useRef(null);
  const coverInputRef = useRef(null);
  const coverSectionRef = useRef(null);

  // ── DRAFT STATE ──
  const [draftId, setDraftId] = useState(null);
  const [recreateFromId, setRecreateFromId] = useState(null);
  const [recreateSourceTitle, setRecreateSourceTitle] = useState("");
  const [isRecreateCoverLoading, setIsRecreateCoverLoading] = useState(false);
  const [recreateCoverNeedsUpload, setRecreateCoverNeedsUpload] = useState(false);
  const [recreateCoverAttention, setRecreateCoverAttention] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  // Skip the load-draft effect when we just created the draft locally via manual save
  const skipNextDraftLoadRef = useRef(false);
  const saveInFlightRef = useRef(false);

  // ── CROP STATE ──
  const [cropImageUrl, setCropImageUrl] = useState(null);
  const [pendingCropName, setPendingCropName] = useState(null);
  const [pendingCropType, setPendingCropType] = useState(null);
  const [originalCoverUrl, setOriginalCoverUrl] = useState(null); // kept around for re-crop
  const [originalCoverMeta, setOriginalCoverMeta] = useState(null); // { name, type }

  const [formData, setFormData] = useState({
    title: "", location: "", description: "", organizer: "",
    email: "", phoneCode: "+33 ", phone: "", date: "", time: "",
    ticketPrice: "", currency: isGuruDomain ? "INR" : "EUR", isFree: false, isPrivate: false,
    allowUserInvitations: true, allowExternalMatching: false,
    matchingStartBefore: "", matchingStartAfter: "",
    matchingCategories: [], requireParticipantPhone: false, capacity: "",
    hasTables: false, numberOfTables: "", seatsPerTable: "",
    tableTimerSeat: null, tableTimerRotation: null,
  });

  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [hasPublishedEvents, setHasPublishedEvents] = useState(null); // null = loading
  const isEdit = !!eventId;
  const isLight = theme === "light";
  const t = translations[lang];

  const C = isLight
    ? { bg:"#f4f5ff", fg:"#1e293b", card:"#ffffff", border:"rgba(99,102,241,0.18)",
        inputBg:"rgba(237,240,255,0.85)", primary:"#6366f1", accent:"#8b5cf6",
        muted:"#64748b", divider:"rgba(99,102,241,0.09)",
        prevBg:"#ffffff", prevBorder:"rgba(99,102,241,0.14)" }
    : { bg:"#0d1525", fg:"#e2e8f0", card:"#152035", border:"rgba(148,163,252,0.13)",
        inputBg:"rgba(15,28,55,0.7)", primary:"#6366f1", accent:"#8b5cf6",
        muted:"#94a3b8", divider:"rgba(148,163,252,0.07)",
        prevBg:"#192640", prevBorder:"rgba(148,163,252,0.11)" };

  // ── Parse URL params (?draft=123 | ?recreate=456) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const recreateParam = params.get("recreate");
    const draftParam = params.get("draft");
    if (!isEdit && recreateParam) {
      const parsed = parseInt(recreateParam, 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        setRecreateFromId(parsed);
        setDraftId(null);
        return;
      }
    }
    if (draftParam && !isEdit) {
      setDraftId(parseInt(draftParam));
    }
  }, [isEdit]);

  // ── Load draft data ──
  useEffect(() => {
    if (!draftId || !token) return;
    // When we set draftId ourselves after a manual save, the latest data
    // is already in the form — don't overwrite the user's in-progress input.
    if (skipNextDraftLoadRef.current) {
      skipNextDraftLoadRef.current = false;
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/my_drafts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        const drafts = await res.json();
        const draft = drafts.find(d => d.id === draftId);
        if (!draft) return;

        const [email, phone = ""] = (draft.organizer_contact || " | ").split(" | ");
        setFormData({
          title: draft.title || "",
          location: draft.location || "",
          description: draft.description || "",
          organizer: draft.organizer_name || "",
          email: email || "",
          phoneCode: "+33 ",
          phone: phone.replace(/^\+\d+/, "").trim(),
          date: draft.event_date || "",
          time: draft.event_time?.slice(0, 5) || "",
          ticketPrice: draft.ticket_price?.toString() || "",
          currency: draft.currency || (isGuruDomain ? "INR" : "EUR"),
          isFree: draft.ticket_price === 0 || draft.ticket_price === "0",
          isPrivate: draft.is_private || false,
          allowUserInvitations: draft.allow_user_invitations !== false,
          allowExternalMatching: draft.allow_external_matching || false,
          matchingStartBefore: draft.matching_start_before || "",
          matchingStartAfter: draft.matching_start_after || "",
          matchingCategories: draft.matching_categories || [],
          requireParticipantPhone: draft.require_participant_phone || false,
          capacity: draft.capacity ? String(draft.capacity) : "",
          hasTables: draft.has_tables || false,
          numberOfTables: draft.number_of_tables ? String(draft.number_of_tables) : "",
          seatsPerTable: draft.seats_per_table ? String(draft.seats_per_table) : "",
          tableTimerSeat: draft.table_timer_seat ?? null,
          tableTimerRotation: draft.table_timer_rotation ?? null,
        });
        if (draft.latitude && draft.longitude) setPosition({ latitude: draft.latitude, longitude: draft.longitude });
        setShowMap(!!draft.latitude); setIsAddressSelected(!!draft.latitude);
        const coverUrl = draft.event_cover_url || draft.cover_url;
        if (coverUrl) setCoverPreview(coverUrl);
      } catch (e) {
        console.error("Error loading draft:", e);
      }
    })();
  }, [draftId, token]);

  // ── Recreate from an already published event (?recreate=id) ──
  useEffect(() => {
    if (isEdit || !token || !recreateFromId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/events/${recreateFromId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (!data?.is_mine) return;
        setRecreateSourceTitle(data.title || "");

        const [email, phone = ""] = (data.organizer_contact || " | ").split(" | ");
        setFormData({
          title: data.title || "",
          location: data.location || "",
          description: data.description || "",
          organizer: data.organizer_name || "",
          email: email || "",
          phoneCode: "+33 ",
          phone: phone.replace(/^\+\d+/, "").trim(),
          date: "",
          time: "",
          ticketPrice: data.ticket_price?.toString() || "",
          currency: data.currency || (isGuruDomain ? "INR" : "EUR"),
          isFree: data.ticket_price === 0 || data.is_free,
          isPrivate: data.is_private || false,
          allowUserInvitations: data.allow_user_invitations !== false,
          allowExternalMatching: data.allow_external_matching || false,
          matchingStartBefore: "",
          matchingStartAfter: "",
          matchingCategories: data.matching_categories || [],
          requireParticipantPhone: data.require_participant_phone || false,
          capacity: data.capacity ? String(data.capacity) : "",
          hasTables: data.has_tables || false,
          numberOfTables: data.number_of_tables ? String(data.number_of_tables) : "",
          seatsPerTable: data.seats_per_table ? String(data.seats_per_table) : "",
          tableTimerSeat: data.table_timer_seat ?? null,
          tableTimerRotation: data.table_timer_rotation ?? null,
        });

        if (data.latitude && data.longitude) {
          setPosition({ latitude: data.latitude, longitude: data.longitude });
          setShowMap(true);
          setIsAddressSelected(true);
        } else {
          setPosition(null);
          setShowMap(false);
          setIsAddressSelected(false);
        }

        if (data.cover_url) {
          setCoverFile(null);
          setRecreateCoverNeedsUpload(false);
          setIsRecreateCoverLoading(true);
          setCoverPreview(data.cover_url);
          setOriginalCoverUrl(data.cover_url);
          const inferredType = data.cover_url.toLowerCase().endsWith(".png")
            ? "image/png"
            : data.cover_url.toLowerCase().endsWith(".webp")
            ? "image/webp"
            : "image/jpeg";
          setOriginalCoverMeta({
            name: `${(data.title || "event").trim() || "event"}-cover.${inferredType === "image/png" ? "png" : inferredType === "image/webp" ? "webp" : "jpg"}`,
            type: inferredType,
          });

          // Best effort: include existing cover as File for POST /api/events
          try {
            const cRes = await fetch(data.cover_url, { mode: "cors" });
            if (cRes.ok) {
              const blob = await cRes.blob();
              const ext =
                blob.type === "image/png"
                  ? "png"
                  : blob.type === "image/webp"
                  ? "webp"
                  : "jpg";
              const file = new File(
                [blob],
                `${(data.title || "event").trim() || "event"}-cover.${ext}`,
                { type: blob.type || inferredType }
              );
              setCoverFile(file);
              setRecreateCoverNeedsUpload(false);
            } else {
              setRecreateCoverNeedsUpload(true);
            }
          } catch {
            // Preview stays visible, but ask user to re-upload before publish.
            setRecreateCoverNeedsUpload(true);
          } finally {
            setIsRecreateCoverLoading(false);
          }
        } else {
          setIsRecreateCoverLoading(false);
        }
      } catch (e) {
        setIsRecreateCoverLoading(false);
        console.error("Error recreating from event:", e);
      }
    })();
  }, [isEdit, token, recreateFromId, isGuruDomain]);

  // ── MANUAL SAVE DRAFT (button click only) ──
  const handleSaveDraft = async () => {
    if (!token || isEdit) return;
    if (saveInFlightRef.current) return;

    const hasAnyData = formData.title.trim() || formData.description.trim() || formData.location.trim() || formData.date;
    if (!hasAnyData) {
      toast(t.requiredField, "error");
      return;
    }

    saveInFlightRef.current = true;
    setSavingDraft(true);

    try {
      const body = new FormData();
      const fields = {
        title: formData.title.trim() || "",
        description: formData.description.trim() || "",
        location: formData.location.trim() || "",
        latitude: position?.latitude,
        longitude: position?.longitude,
        event_date: formData.date || null,
        event_time: formData.time || null,
        timezone: "UTC",
        organizer_name: formData.organizer.trim() || "",
        organizer_contact: `${formData.email.trim()} | ${formData.phoneCode}${formData.phone.replace(/\s/g, '')}`,
        ticket_price: formData.isFree ? 0 : parseFloat(formData.ticketPrice) || 0,
        currency: formData.currency,
        is_private: formData.isPrivate,
        require_approval: true,
        allow_user_invitations: formData.allowUserInvitations,
        allow_external_matching: formData.allowExternalMatching,
        matching_start_before: formData.matchingStartBefore || null,
        matching_start_after: formData.matchingStartAfter || null,
        require_participant_phone: formData.requireParticipantPhone,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        has_tables: formData.hasTables,
        number_of_tables: formData.hasTables && formData.numberOfTables ? parseInt(formData.numberOfTables) : null,
        seats_per_table: formData.hasTables && formData.seatsPerTable ? parseInt(formData.seatsPerTable) : null,
        table_timer_seat: formData.tableTimerSeat,
        table_timer_rotation: formData.tableTimerRotation,
      };
      Object.entries(fields).forEach(([k, v]) => { if (v !== null && v !== undefined) body.append(k, v); });
      (formData.matchingCategories || []).forEach((cat) => body.append("matching_categories", cat));
      if (coverFile) body.append("cover", coverFile);

      const url = draftId
        ? `${API_URL}/api/events/draft/${draftId}`
        : `${API_URL}/api/events/draft`;
      const method = draftId ? "PUT" : "POST";

      const res = await fetch(url, { method, headers: { Authorization: `Bearer ${token}` }, body });
      if (!res.ok) throw new Error();
      const data = await res.json();

      if (!draftId && data.id) {
        // Mark so the load-draft effect doesn't reset the form we just sent.
        skipNextDraftLoadRef.current = true;
        setDraftId(data.id);
        window.history.replaceState(null, "", `/events/create?draft=${data.id}`);
      }

      toast(draftId ? t.draftUpdated : t.draftSaved, "success", 3000);
    } catch (err) {
      toast(t.draftError, "error");
    } finally {
      saveInFlightRef.current = false;
      setSavingDraft(false);
    }
  };

  // ── EFFECTS ──
  useEffect(() => {
    if (!token || isEdit) { setHasPublishedEvents(isEdit ? true : false); return; }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/events/first_event_check`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setHasPublishedEvents(!data.is_first_event);
      } catch {
        setHasPublishedEvents(true);
      }
    })();
  }, [token, isEdit]);

  useEffect(() => {
    return () => { if (coverPreview?.startsWith('blob:')) URL.revokeObjectURL(coverPreview); };
  }, [coverPreview]);

  useEffect(() => {
    if (!isEdit || !token) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/events/${eventId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error();
        const data = await res.json();
        if (!data.is_mine) { navigate("/events"); return; }
        const [email, phone = ""] = (data.organizer_contact || " | ").split(" | ");
        setFormData({
          title: data.title || "", location: data.location || "", description: data.description || "",
          organizer: data.organizer_name || "", email: email || "",
          phoneCode: "+33 ",
          phone: phone.replace(/^\+\d+/, "").trim(),
          date: data.event_date?.split("T")[0] || "", time: data.event_time?.slice(0, 5) || "",
          ticketPrice: data.ticket_price?.toString() || "", currency: data.currency || (isGuruDomain ? "INR" : "EUR"),
          isFree: data.ticket_price === 0 || data.is_free, isPrivate: data.is_private || false,
          allowUserInvitations: data.allow_user_invitations !== false,
          allowExternalMatching: data.allow_external_matching || false,
          matchingStartBefore: data.matching_start_before || "", matchingStartAfter: data.matching_start_after || "",
          matchingCategories: data.matching_categories || [], requireParticipantPhone: data.require_participant_phone || false,
          capacity: data.capacity ? String(data.capacity) : "",
          hasTables: data.has_tables || false,
          numberOfTables: data.number_of_tables ? String(data.number_of_tables) : "",
          seatsPerTable: data.seats_per_table ? String(data.seats_per_table) : "",
          tableTimerSeat: data.table_timer_seat ?? null,
          tableTimerRotation: data.table_timer_rotation ?? null,
        });
        if (data.latitude && data.longitude) setPosition({ latitude: data.latitude, longitude: data.longitude });
        setShowMap(!!data.latitude); setIsAddressSelected(!!data.latitude);
        if (data.cover_url) setCoverPreview(data.cover_url);
      } catch { navigate("/events"); }
    })();
  }, [isEdit, eventId, token, navigate, lang]);

  const searchAddress = async (query) => {
    if (!query.trim()) { setSuggestions([]); setShowSuggestions(false); setIsSearching(false); return; }
    setIsSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1&accept-language=${lang === "fr" ? "fr" : "en"}`,
        { headers: { "User-Agent": "CreateEvents/1.0" } }
      );
      const data = await res.json();
      setSuggestions(data); setShowSuggestions(data.length > 0);
    } catch { setSuggestions([]); setShowSuggestions(false); }
    finally { setIsSearching(false); }
  };

  useEffect(() => {
    if (!isAddressSelected && formData.location) {
      const t = setTimeout(() => searchAddress(formData.location), 300);
      return () => clearTimeout(t);
    } else if (!formData.location) { setSuggestions([]); setShowSuggestions(false); }
  }, [formData.location, isAddressSelected]);

  useEffect(() => {
    const h = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          locationInputRef.current && !locationInputRef.current.contains(e.target))
        setShowSuggestions(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── HANDLERS ──
  const handleAddressSelect = (address) => {
    const lat = parseFloat(address.lat);
    const lon = parseFloat(address.lon);
    if (!isFinite(lat) || !isFinite(lon)) return;
    setFormData(p => ({ ...p, location: address.display_name }));
    setPosition({ latitude: lat, longitude: lon });
    setSuggestions([]);
    setShowSuggestions(false);
    setIsAddressSelected(true);
    setShowMap(true);
    // jumpTo выполняется отдельным эффектом ниже, после монтирования карты.
  };

  // Когда позиция меняется и карта смонтирована — паним её.
  // Раньше jumpTo вызывался синхронно в handleAddressSelect ещё до того,
  // как Map успел отрендериться (showMap только что выставлен в true),
  // поэтому первый клик казался «не сработавшим» и второй раз приходилось
  // выбирать тот же адрес заново.
  useEffect(() => {
    if (!showMap || !position) return;
    const id = requestAnimationFrame(() => {
      mapRef.current?.jumpTo({
        center: [position.longitude, position.latitude],
        zoom: 16,
      });
    });
    return () => cancelAnimationFrame(id);
  }, [showMap, position]);

  const handleLocationChange = (e) => {
    setFormData(p => ({ ...p, location: e.target.value }));
    setIsAddressSelected(false);
    if (!e.target.value) setPosition(null);
  };

  const fetchAddressFromCoordinates = async (pos) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.latitude}&lon=${pos.longitude}&zoom=18&addressdetails=1&accept-language=${lang === "fr" ? "fr" : "en"}`,
        { headers: { "User-Agent": "CreateEvents/1.0" } }
      );
      const data = await res.json();
      setFormData(p => ({ ...p, location: data?.display_name || (lang === "fr" ? "Sélectionné sur la carte" : "Selected on map") }));
      setIsAddressSelected(true);
    } catch {
      setFormData(p => ({ ...p, location: lang === "fr" ? "Sélectionné sur la carte" : "Selected on map" }));
      setIsAddressSelected(true);
    }
  };

  const handleMapClick = (e) => {
    const pos = { longitude: e.lngLat.lng, latitude: e.lngLat.lat };
    setPosition(pos); setIsAddressSelected(false); fetchAddressFromCoordinates(pos);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "isFree") setFormData(p => ({ ...p, isFree: checked, ticketPrice: checked ? "0" : p.ticketPrice }));
    else if (name === "hasTables") setFormData(p => ({ ...p, hasTables: checked, numberOfTables: checked ? p.numberOfTables : "", seatsPerTable: checked ? p.seatsPerTable : "" }));
    else setFormData(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  // Нормализация телефона под фиксированный +33:
  //   удаляет пробелы/тире/скобки, режет префиксы +33 / 0033 / 33 / ведущий 0,
  //   оставляет только цифры (макс 9 — длина французского национального номера).
  const normalizePhoneInput = (raw) => {
    if (!raw) return "";
    let s = String(raw).replace(/[\s().\-]/g, "");
    if (s.startsWith("+33")) s = s.slice(3);
    else if (s.startsWith("0033")) s = s.slice(4);
    else if (s.startsWith("33") && s.length >= 11) s = s.slice(2);
    if (s.startsWith("0")) s = s.slice(1);
    s = s.replace(/\D/g, "");
    return s.slice(0, 9);
  };

  const handlePhoneChange = (e) => {
    setFormData(p => ({ ...p, phone: normalizePhoneInput(e.target.value) }));
  };

  const handlePhonePaste = (e) => {
    const pasted = e.clipboardData?.getData("text");
    if (pasted == null) return;
    e.preventDefault();
    setFormData(p => ({ ...p, phone: normalizePhoneInput(pasted) }));
  };

  const handleCoverUpload = (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    if (!file.type.startsWith('image/')) { setCoverError(t.invalidImageType); toast(t.invalidImageType, "error"); return; }
    if (file.size > 5 * 1024 * 1024) { setCoverError(t.fileTooLarge); toast(t.fileTooLarge, "error"); return; }
    setCoverError("");
    setIsRecreateCoverLoading(false);
    setRecreateCoverNeedsUpload(false);
    setRecreateCoverAttention(false);
    if (originalCoverUrl) URL.revokeObjectURL(originalCoverUrl);
    const url = URL.createObjectURL(file);
    setOriginalCoverUrl(url);
    setOriginalCoverMeta({ name: file.name, type: file.type });
    setPendingCropName(file.name);
    setPendingCropType(file.type);
    setCropImageUrl(url);
  };

  const handleCropApply = (croppedFile) => {
    if (coverPreview && coverPreview.startsWith('blob:')) URL.revokeObjectURL(coverPreview);
    setCoverFile(croppedFile);
    setCoverPreview(URL.createObjectURL(croppedFile));
    setRecreateCoverNeedsUpload(false);
    setRecreateCoverAttention(false);
    setCropImageUrl(null);
    setPendingCropName(null);
    setPendingCropType(null);
    toast(t.coverUploaded, "success");
  };

  const handleCropCancel = () => {
    setCropImageUrl(null);
    setPendingCropName(null);
    setPendingCropType(null);
  };

  const handleRecrop = () => {
    if (!originalCoverUrl) return;
    setPendingCropName(originalCoverMeta?.name || 'cover.jpg');
    setPendingCropType(originalCoverMeta?.type || 'image/jpeg');
    setCropImageUrl(originalCoverUrl);
  };

  // Cleanup original blob on unmount
  useEffect(() => {
    return () => { if (originalCoverUrl) URL.revokeObjectURL(originalCoverUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData.title.trim()) { toast(t.requiredField, "error"); return; }
    if (!formData.description.trim()) { toast(t.requiredField, "error"); return; }
    if (!formData.date) { toast(t.requiredField, "error"); return; }
    if (!position) { toast(t.addressRequired, "error"); return; }
    if (!coverPreview && !isEdit) { toast(t.coverRequired, "error"); return; }
    if (!isEdit && recreateFromId && isRecreateCoverLoading) {
      const msg = lang === "fr"
        ? "La couverture est en cours de préparation. Veuillez patienter."
        : "Cover is still being prepared. Please wait a moment.";
      toast(msg, "info");
      coverSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setRecreateCoverAttention(true);
      window.setTimeout(() => setRecreateCoverAttention(false), 1400);
      return;
    }
    if (!isEdit && recreateFromId && !coverFile) {
      const msg = lang === "fr"
        ? "La couverture n'a pas pu être copiée automatiquement. Veuillez la re-téléverser avant publication."
        : "Cover could not be copied automatically. Please re-upload it before publish.";
      toast(msg, "error");
      coverSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setRecreateCoverAttention(true);
      window.setTimeout(() => setRecreateCoverAttention(false), 1400);
      return;
    }
    if (!formData.isFree) {
      const price = parseFloat(formData.ticketPrice?.trim());
      if (!formData.ticketPrice?.trim()) { toast(t.ticketPriceRequired, "error"); return; }
      if (isNaN(price) || price < 0) { toast(t.invalidTicketPrice, "error"); return; }
      if (price > 9999999999.99) { toast(t.ticketPriceTooHigh, "error"); return; }
    }
    if (formData.hasTables && (!formData.numberOfTables || parseInt(formData.numberOfTables) < 1 || !formData.seatsPerTable || parseInt(formData.seatsPerTable) < 2)) {
      toast(t.tablesRequired, "error"); return;
    }
    setStatus(isEdit ? "updating" : "loading"); setMessage("");
    try {
      const body = new FormData();
      const eventData = {
        title: formData.title.trim(), description: formData.description.trim(),
        location: formData.location.trim(), latitude: position?.latitude, longitude: position?.longitude,
        event_date: formData.date, event_time: formData.time || "19:00", timezone: "UTC",
        organizer_name: formData.organizer.trim(),
        organizer_contact: `${formData.email.trim()} | ${formData.phoneCode}${formData.phone.replace(/\s/g, '')}`,
        ticket_price: formData.isFree ? 0 : parseFloat(formData.ticketPrice) || 0,
        currency: formData.currency, is_private: formData.isPrivate, require_approval: true,
        allow_user_invitations: formData.allowUserInvitations,
        allow_external_matching: formData.allowExternalMatching,
        matching_start_before: formData.matchingStartBefore || null,
        matching_start_after: formData.matchingStartAfter || null,
        require_participant_phone: formData.requireParticipantPhone,
        capacity: formData.capacity ? parseInt(formData.capacity) : null,
        has_tables: formData.hasTables,
        number_of_tables: formData.hasTables && formData.numberOfTables ? parseInt(formData.numberOfTables) : null,
        seats_per_table: formData.hasTables && formData.seatsPerTable ? parseInt(formData.seatsPerTable) : null,
        table_timer_seat: formData.tableTimerSeat,
        table_timer_rotation: formData.tableTimerRotation,
      };
      Object.entries(eventData).forEach(([k, v]) => { if (v !== null && v !== undefined) body.append(k, v); });
      formData.matchingCategories.forEach(cat => body.append("matching_categories", cat));
      if (coverFile) body.append('cover', coverFile);

      const res = await fetch(
        isEdit ? `${API_URL}/api/events/${eventId}` : `${API_URL}/api/events`,
        { method: isEdit ? "PUT" : "POST", headers: { Authorization: `Bearer ${token}` }, body }
      );
      if (!res.ok) {
        let msg = isEdit ? t.errorUpdatingEvent : t.errorCreatingEvent;
        try { const d = await res.json(); if (d.detail) msg = Array.isArray(d.detail) ? d.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join('\n') : d.detail; } catch {}
        throw new Error(msg);
      }
      if (!isEdit) {
        // If submitting from a draft, delete the draft after successful creation
        if (draftId) {
          try {
            await fetch(`${API_URL}/api/events/draft/${draftId}`, {
              method: "DELETE",
              headers: { Authorization: `Bearer ${token}` },
            });
          } catch {}
        }
        toast(t.eventSubmitted, "success", 6000); setTimeout(() => navigate("/events"), 1500);
      }
      else { setStatus("success"); setMessage(t.eventUpdated); toast(t.eventUpdated, "success"); setTimeout(() => navigate(`/events/${eventId}`), 1500); }
    } catch (err) {
      const msg = err.message || (isEdit ? t.errorUpdatingEvent : t.errorCreatingEvent);
      setStatus("error"); setMessage(msg); toast(msg, "error");
    }
  };

  const handleCancel = () => navigate("/events");

  const isSubmitting = status === "loading" || status === "updating";

  // Единый источник истины — что именно мешает создать ивент.
  const validationIssues = (() => {
    const out = [];
    if (!formData.title.trim()) out.push(t.issueTitle);
    if (!formData.description.trim()) out.push(t.issueDescription);
    if (!formData.date) out.push(t.issueDate);
    if (!position) out.push(t.issueLocation);
    if (!coverPreview && !isEdit) out.push(t.issueCover);
    if (!formData.isFree) {
      const priceStr = (formData.ticketPrice || "").toString().trim();
      const price = parseFloat(priceStr);
      if (!priceStr || isNaN(price) || price < 0 || price > 9999999999.99) {
        out.push(t.issueTicketPrice);
      }
    }
    if (formData.hasTables) {
      const nT = parseInt(formData.numberOfTables);
      const sT = parseInt(formData.seatsPerTable);
      if (!nT || nT < 1 || !sT || sT < 2) out.push(t.issueTablesConfig);
    }
    return out;
  })();
  const isDisabled = validationIssues.length > 0 || (!isEdit && recreateFromId && isRecreateCoverLoading);
  const isFirstFreeEvent = !isEdit && hasPublishedEvents === false;

  // derived table stats
  const tablesCount = (formData.hasTables && formData.numberOfTables && parseInt(formData.numberOfTables) >= 1)
    ? parseInt(formData.numberOfTables)
    : null;
  const totalTableSeats = (tablesCount && formData.seatsPerTable && parseInt(formData.seatsPerTable) >= 2)
    ? tablesCount * parseInt(formData.seatsPerTable)
    : null;

  // ── RENDER ──
  return (
    <div className="ce-root">
      <style jsx>{`
        * { box-sizing: border-box; }
        .ce-root {
          min-height: 100vh; background: ${C.bg}; color: ${C.fg};
          font-family: 'DM Sans', 'Helvetica Neue', Arial, sans-serif;
          padding-bottom: 90px;
        }
        .ce-page {
          width: 100%;
          max-width: 1120px;
          margin: 0 auto;
          padding: 0 clamp(18px, 3vw, 40px);
          box-sizing: border-box;
        }

        /* ─── KEYFRAMES ─────────────────────────────────────────────── */
        @keyframes ce-slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes ce-slide-down {
          from { opacity: 0; transform: translateY(-10px) scaleY(0.95); }
          to   { opacity: 1; transform: translateY(0) scaleY(1); }
        }
        @keyframes ce-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes ce-scale-in {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes ce-pop {
          0%   { transform: scale(0.92); opacity: 0.6; }
          55%  { transform: scale(1.04); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes ce-bar-up {
          from { opacity: 0; transform: translate(-50%, 24px) scale(0.96); }
          to   { opacity: 1; transform: translate(-50%, 0) scale(1); }
        }
        @keyframes ce-bar-up-mobile {
          from { opacity: 0; transform: translateY(18px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes ce-shimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes ce-pulse {
          0%,100% { box-shadow: 0 0 0 2px ${C.primary}30; }
          50%     { box-shadow: 0 0 0 5px ${C.primary}12; }
        }
        @keyframes ce-num-pop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.12); color: #a78bfa; }
          100% { transform: scale(1); }
        }

        /* ─── HEADER ─────────────────────────────────────────────────── */
        .ce-header {
          margin: 0; padding: 60px 0 28px;
          border-bottom: 1px solid ${C.divider};
          animation: ce-slide-up 0.62s cubic-bezier(0.4,0,0.2,1) both;
        }
        .ce-header-eyebrow {
          font-size: 0.68rem; font-weight: 700; letter-spacing: 0.14em;
          text-transform: uppercase; color: ${C.primary}; margin-bottom: 8px;
          animation: ce-fade-in 0.5s ease 0.12s both;
        }
        .ce-header-title {
          font-size: 1.875rem; font-weight: 700; letter-spacing: -0.03em;
          line-height: 1.1; margin: 0 0 5px;
          animation: ce-slide-up 0.6s cubic-bezier(0.4,0,0.2,1) 0.06s both;
        }
        .ce-header-sub {
          font-size: 0.875rem; color: ${C.muted}; margin: 0;
          animation: ce-fade-in 0.55s ease 0.18s both;
        }
        .ce-header-recreate-badge {
          margin-top: 10px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 999px;
          border: 1px solid ${C.border};
          background: ${C.inputBg};
          color: ${C.primary};
          font-size: 0.78rem;
          font-weight: 600;
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ─── MAIN LAYOUT (hero preview + form) ─────────────────────── */
        .ce-layout {
          margin: 0; padding: 0 0 36px;
          display: grid; grid-template-columns: 1fr;
          gap: 40px; align-items: start;
        }
        .ce-layout-hero {
          width: 100%;
          max-width: 460px;
          margin: 0 auto;
          padding-top: 28px;
          animation: ce-slide-up 0.7s cubic-bezier(0.4,0,0.2,1) 0.1s both;
        }
        .ce-layout-hero .ce-first-free-banner { max-width: 100%; }
        .ce-layout-hero .prev-card { margin-left: auto; margin-right: auto; }

        /* ─── FORM COL ───────────────────────────────────────────────── */
        .ce-form-col {
          padding-top: 0; padding-bottom: 36px; min-width: 0;
          animation: ce-fade-in 0.5s ease 0.25s both;
        }
        .ce-form { display: flex; flex-direction: column; }

        /* ─── SECTION ────────────────────────────────────────────────── */
        .ce-section {
          display: grid; grid-template-columns: 160px 1fr;
          gap: 20px; padding: 24px 0;
          border-bottom: 1px solid ${C.divider};
        }
        .ce-section:last-of-type { border-bottom: none; }
        .ce-section-num {
          font-size: 0.62rem; font-weight: 700; letter-spacing: 0.12em;
          text-transform: uppercase; color: ${C.primary}; opacity: 0.55; margin-bottom: 3px;
        }
        .ce-section-title { font-size: 0.82rem; font-weight: 600; margin: 0 0 3px; }
        .ce-section-desc { font-size: 0.71rem; color: ${C.muted}; line-height: 1.45; margin: 0; }
        .ce-section-body { display: flex; flex-direction: column; gap: 12px; }

        /* ─── GRID ───────────────────────────────────────────────────── */
        .ce-row { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; }

        /* ─── FORM ELEMENTS ──────────────────────────────────────────── */
        .ce-group { display: flex; flex-direction: column; gap: 4px; }
        .ce-label {
          font-size: 0.72rem; font-weight: 500; color: ${C.muted};
          display: flex; align-items: center; gap: 3px;
        }
        .ce-req { color: #f87171; }
        .ce-input, .ce-textarea, .ce-select {
          width: 100%; background: ${C.inputBg}; border: 1px solid ${C.border};
          color: ${C.fg}; padding: 9px 12px; border-radius: 7px;
          font-size: 0.875rem; font-family: inherit; outline: none;
          transition: border-color 0.2s, box-shadow 0.2s, transform 0.18s, background 0.2s;
        }
        .ce-input:focus, .ce-textarea:focus, .ce-select:focus {
          border-color: ${C.primary}; box-shadow: 0 0 0 3px ${C.primary}18;
          transform: translateY(-1px);
        }
        .ce-input:hover:not(:focus), .ce-textarea:hover:not(:focus), .ce-select:hover:not(:focus) {
          border-color: ${C.primary}60;
        }
        .ce-textarea { resize: vertical; min-height: 100px; line-height: 1.6; }
        .ce-err {
          font-size: 0.71rem; color: #f87171; font-weight: 500;
          animation: ce-slide-down 0.25s ease;
        }

        /* ─── COVER ──────────────────────────────────────────────────── */
        .ce-cover {
          position: relative; width: 100%; padding-top: 44%;
          background: ${C.inputBg}; border-radius: 8px; overflow: hidden;
          cursor: pointer; border: 1.5px dashed ${C.border};
          transition: border-color 0.2s, transform 0.22s cubic-bezier(0.4,0,0.2,1), box-shadow 0.22s;
        }
        .ce-cover:hover {
          border-color: ${C.primary};
          transform: scale(1.008);
          box-shadow: 0 6px 24px ${C.primary}22;
        }
        .ce-cover-img {
          position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
          animation: ce-fade-in 0.45s ease;
        }
        .ce-cover-empty {
          position: absolute; inset: 0;
          display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 5px;
          transition: opacity 0.2s;
        }
        .ce-cover:hover .ce-cover-empty { opacity: 0.7; }
        .ce-cover-lbl { font-size: 0.78rem; font-weight: 500; opacity: 0.55; }
        .ce-cover-sub { font-size: 0.68rem; color: ${C.muted}; opacity: 0.4; }
        .ce-cover-change {
          position: absolute; bottom: 9px; left: 50%; transform: translateX(-50%);
          background: rgba(0,0,0,0.58); color: #fff; border: none;
          padding: 4px 13px; border-radius: 20px; font-size: 0.72rem; font-weight: 500;
          cursor: pointer; white-space: nowrap;
          transition: background 0.2s, transform 0.2s;
        }
        .ce-cover-change:hover { background: rgba(0,0,0,0.78); transform: translateX(-50%) scale(1.04); }
        .ce-cover-action {
          background: rgba(0,0,0,0.62); color: #fff; border: none;
          padding: 5px 13px; border-radius: 20px; font-size: 0.72rem; font-weight: 500;
          cursor: pointer; white-space: nowrap; font-family: inherit;
          transition: background 0.2s, transform 0.2s;
        }
        .ce-cover-action:hover { background: rgba(0,0,0,0.82); transform: scale(1.05); }
        .ce-cover-input { position: absolute; width: 0; height: 0; opacity: 0; overflow: hidden; pointer-events: none; }
        .ce-cover-attention .ce-cover {
          border-color: #f59e0b;
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.22);
          animation: ce-cover-attention-pulse 0.6s ease 2;
        }
        .ce-btn-primary.ce-attention {
          box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.24);
          animation: ce-cover-attention-pulse 0.6s ease 2;
        }
        @keyframes ce-cover-attention-pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }

        /* ─── RANGE SLIDERS ──────────────────────────────────────────── */
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 20px; height: 20px; border-radius: 50%;
          background: #fff; border: 2.5px solid currentColor;
          box-shadow: 0 1px 6px rgba(0,0,0,0.22); cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        input[type="range"]::-moz-range-thumb {
          width: 18px; height: 18px; border-radius: 50%;
          background: #fff; border: 2.5px solid currentColor;
          box-shadow: 0 1px 6px rgba(0,0,0,0.22); cursor: pointer;
        }
        input[type="range"]:hover::-webkit-slider-thumb {
          transform: scale(1.15); box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        }
        input[type="range"]:active::-webkit-slider-thumb {
          transform: scale(1.25);
        }
        .ce-loc-wrap { position: relative; }
        .ce-input.open { border-bottom-left-radius: 0; border-bottom-right-radius: 0; }
        .ce-sugg {
          position: absolute; top: 100%; left: 0; right: 0;
          background: ${C.card}; border: 1px solid ${C.border}; border-top: none;
          border-radius: 0 0 7px 7px; z-index: 300;
          max-height: 220px; overflow-y: auto; box-shadow: 0 8px 24px rgba(0,0,0,0.18);
          animation: ce-slide-down 0.22s cubic-bezier(0.4,0,0.2,1);
          transform-origin: top center;
        }
        .ce-sugg-item {
          padding: 9px 12px; cursor: pointer;
          border-bottom: 1px solid ${C.divider};
          transition: background 0.12s, padding-left 0.15s;
        }
        .ce-sugg-item:last-child { border-bottom: none; }
        .ce-sugg-item:hover { background: ${C.inputBg}; padding-left: 15px; }
        .ce-sugg-main { font-size: 0.8rem; font-weight: 500; margin-bottom: 1px; }
        .ce-sugg-sub { font-size: 0.7rem; color: ${C.muted}; }
        .ce-sugg-state { padding: 9px 12px; font-size: 0.78rem; color: ${C.muted}; font-style: italic; text-align: center; }
        .ce-map-btn {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 10px; background: transparent;
          border: 1px solid ${C.border}; color: ${C.primary};
          font-size: 0.73rem; font-weight: 500; border-radius: 5px;
          cursor: pointer; font-family: inherit;
          transition: background 0.18s, border-color 0.18s, transform 0.18s;
        }
        .ce-map-btn:hover { background: ${C.primary}0e; border-color: ${C.primary}; transform: translateY(-1px); }
        .ce-map-btn:active { transform: scale(0.97); }
        .ce-map-box {
          border-radius: 8px; overflow: hidden; border: 1px solid ${C.border}; height: 280px;
          animation: ce-scale-in 0.32s cubic-bezier(0.4,0,0.2,1);
        }

        /* ─── OPTIONS ────────────────────────────────────────────────── */
        .ce-option {
          display: flex; align-items: flex-start; gap: 9px;
          padding: 11px 13px; background: ${C.inputBg};
          border: 1.5px solid ${C.border}; border-radius: 7px; cursor: pointer;
          transition: border-color 0.2s, background 0.2s, transform 0.18s cubic-bezier(0.4,0,0.2,1), box-shadow 0.2s;
        }
        .ce-option:hover { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(0,0,0,0.09); border-color: ${C.primary}60; }
        .ce-option:active { transform: scale(0.98); }
        .ce-option.on {
          border-color: ${C.primary}; background: ${C.primary}09;
          animation: ce-pop 0.32s cubic-bezier(0.34,1.56,0.64,1);
        }
        .ce-option input[type="radio"], .ce-option input[type="checkbox"] {
          width: 14px; height: 14px; accent-color: ${C.primary};
          cursor: pointer; margin-top: 2px; flex-shrink: 0;
        }
        .ce-opt-title { font-size: 0.83rem; font-weight: 600; margin-bottom: 1px; }
        .ce-opt-desc { font-size: 0.72rem; color: ${C.muted}; line-height: 1.4; margin: 0; }
        .ce-opts-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }

        /* ─── PHONE ──────────────────────────────────────────────────── */
        .ce-phone { display: flex; gap: 6px; align-items: stretch; }
        .ce-phone-code-static {
          flex-shrink: 0;
          display: inline-flex; align-items: center; justify-content: center;
          padding: 0 12px; border-radius: 7px;
          background: ${C.inputBg}; border: 1px solid ${C.border}; color: ${C.muted};
          font-size: 0.875rem; font-weight: 600; letter-spacing: 0.02em;
          user-select: none; cursor: default;
        }

        /* ─── CHIPS ──────────────────────────────────────────────────── */
        .ce-chips { display: flex; flex-wrap: wrap; gap: 6px; }
        .ce-chip {
          display: inline-flex; align-items: center;
          padding: 5px 11px; background: ${C.inputBg};
          border: 1.5px solid ${C.border}; border-radius: 20px;
          cursor: pointer; font-size: 0.75rem; font-weight: 500;
          transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1); user-select: none;
        }
        .ce-chip:hover { transform: scale(1.05) translateY(-1px); border-color: ${C.primary}70; }
        .ce-chip:active { transform: scale(0.96); }
        .ce-chip.on {
          border-color: ${C.primary}; background: ${C.primary}15; color: ${C.primary};
          animation: ce-pop 0.28s cubic-bezier(0.34,1.56,0.64,1);
        }
        .ce-chip input { position: absolute; opacity: 0; width: 0; height: 0; }

        /* ─── REVIEW ─────────────────────────────────────────────────── */
        .ce-review {
          background: ${C.inputBg}; border: 1px solid ${C.border};
          border-radius: 7px; overflow: hidden;
          display: grid; grid-template-columns: 1fr 1fr;
        }
        .ce-rv {
          padding: 9px 13px; border-bottom: 1px solid ${C.divider};
          border-right: 1px solid ${C.divider};
        }
        .ce-rv:nth-child(2n) { border-right: none; }
        .ce-rv.full { grid-column: 1/-1; border-right: none; }
        .ce-rv:nth-last-child(-n+2) { border-bottom: none; }
        .ce-rv.full:last-child { border-bottom: none; }
        .ce-rv-lbl { font-size: 0.62rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: ${C.muted}; margin-bottom: 2px; }
        .ce-rv-val {
          font-size: 0.82rem; font-weight: 600;
          transition: color 0.35s ease;
        }
        .ce-review-hint { font-size: 0.72rem; color: ${C.muted}; line-height: 1.5; margin-top: 9px; font-style: italic; }
        .ce-issues {
          margin-top: 14px; padding: 12px 14px; border-radius: 9px;
          background: ${isLight ? 'rgba(248,113,113,0.08)' : 'rgba(248,113,113,0.10)'};
          border: 1px solid rgba(248,113,113,0.35);
          color: ${isLight ? '#991b1b' : '#fecaca'};
          font-size: 0.78rem; line-height: 1.5;
          animation: ce-fade-in 0.25s ease;
        }
        .ce-issues-title {
          display: flex; align-items: center; gap: 7px;
          font-weight: 700; margin-bottom: 6px; color: #f87171;
        }
        .ce-issues-list { margin: 0; padding-left: 22px; }
        .ce-issues-list li { margin: 2px 0; }

        /* ─── BANNER ─────────────────────────────────────────────────── */
        .ce-banner {
          padding: 11px 16px; border-radius: 7px; font-size: 0.85rem; font-weight: 500; text-align: center; margin-top: 14px;
          animation: ce-slide-up 0.4s cubic-bezier(0.4,0,0.2,1);
        }
        .ce-banner.ok { background: rgba(52,211,153,0.1); border: 1px solid #34d39930; color: #10b981; }
        .ce-banner.err { background: rgba(248,113,113,0.1); border: 1px solid #f8717130; color: #f87171; }

        /* ─── PREVIEW (stacked under banner) ─────────────────────────── */
        .ce-prev-eyebrow {
          font-size: 0.62rem; font-weight: 700; letter-spacing: 0.13em; text-transform: uppercase;
          color: ${C.muted}; margin-bottom: 10px;
          display: flex; align-items: center; gap: 7px;
        }
        .ce-prev-dot {
          width: 7px; height: 7px; border-radius: 50%;
          background: ${C.primary}; flex-shrink: 0;
          box-shadow: 0 0 0 3px ${C.primary}30;
          animation: ce-pulse 2.2s ease-in-out infinite;
        }
        .ce-prev-sub { font-size: 0.68rem; color: ${C.muted}; opacity: 0.5; text-align: center; margin-top: 7px; }
        .ce-layout-hero .ce-prev-eyebrow { justify-content: center; }

        /* ─── PREVIEW CARD ───────────────────────────────────────────── */
        .prev-card {
          background: ${C.prevBg}; border: 1px solid ${C.prevBorder};
          border-radius: 14px; overflow: hidden;
          box-shadow: 0 6px 40px rgba(0,0,0,${isLight ? '0.09' : '0.22'}), 0 1px 4px rgba(0,0,0,0.05);
          transition: box-shadow 0.3s, transform 0.3s;
        }
        .prev-card:hover { transform: translateY(-2px); box-shadow: 0 12px 48px rgba(0,0,0,${isLight ? '0.14' : '0.3'}); }
        .prev-cover {
          position: relative; width: 100%; padding-top: 54%;
          background: ${C.inputBg}; overflow: hidden;
        }
        .prev-cover-img {
          position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
          animation: ce-fade-in 0.5s ease;
          transition: transform 0.4s ease;
        }
        .prev-card:hover .prev-cover-img { transform: scale(1.03); }
        .prev-cover-empty {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, ${C.inputBg}, ${C.bg});
        }
        .prev-badges {
          position: absolute; top: 9px; left: 9px; display: flex; gap: 5px;
        }
        .prev-badge-vis {
          padding: 2px 8px; border-radius: 20px; font-size: 0.6rem; font-weight: 700;
          letter-spacing: 0.06em; text-transform: uppercase;
          background: rgba(0,0,0,0.42); color: rgba(255,255,255,0.88);
          border: 1px solid rgba(255,255,255,0.12); backdrop-filter: blur(6px);
          transition: all 0.3s;
        }
        .prev-badge-price {
          padding: 2px 8px; border-radius: 20px; font-size: 0.6rem; font-weight: 700;
          letter-spacing: 0.04em; text-transform: uppercase;
          background: ${C.primary}; color: #fff;
          animation: ce-pop 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        .prev-body { padding: 14px 15px; display: flex; flex-direction: column; gap: 8px; }
        .prev-title {
          font-size: 0.95rem; font-weight: 700; line-height: 1.25; letter-spacing: -0.02em;
          transition: color 0.3s;
        }
        .prev-placeholder { color: ${C.muted}; font-weight: 400; opacity: 0.35; font-style: italic; }
        .prev-meta {
          display: flex; align-items: flex-start; gap: 6px;
          font-size: 0.75rem; color: ${C.muted}; line-height: 1.4;
          animation: ce-fade-in 0.3s ease;
        }
        .prev-icon { flex-shrink: 0; margin-top: 1px; opacity: 0.5; }
        .prev-loc { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .prev-desc {
          font-size: 0.73rem; color: ${C.muted}; line-height: 1.55; margin: 0;
          padding-top: 8px; border-top: 1px solid ${C.divider};
          animation: ce-fade-in 0.35s ease;
        }
        .prev-org {
          display: flex; align-items: center; gap: 8px;
          padding-top: 8px; border-top: 1px solid ${C.divider};
          animation: ce-slide-up 0.3s ease;
        }
        .prev-org-av {
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          background: linear-gradient(135deg, ${C.primary}, ${C.accent});
          display: flex; align-items: center; justify-content: center;
          font-size: 0.68rem; font-weight: 700; color: #fff;
          transition: transform 0.2s;
        }
        .prev-org:hover .prev-org-av { transform: scale(1.12); }
        .prev-org-lbl { font-size: 0.6rem; color: ${C.muted}; text-transform: uppercase; letter-spacing: 0.06em; }
        .prev-org-name { font-size: 0.75rem; font-weight: 600; }
        .prev-ai-tag {
          display: inline-flex; align-items: center; gap: 4px;
          padding: 3px 9px; border-radius: 20px; font-size: 0.63rem; font-weight: 600;
          background: ${C.accent}15; color: ${C.accent};
          border: 1px solid ${C.accent}28; align-self: flex-start;
          animation: ce-pop 0.3s cubic-bezier(0.34,1.56,0.64,1);
          transition: transform 0.18s;
        }
        .prev-ai-tag:hover { transform: scale(1.05); }

        /* ─── ACTION BAR ─────────────────────────────────────────────── */
        .ce-bar {
          position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%);
          z-index: 500; width: auto; max-width: calc(100vw - 48px);
          background: ${isLight ? 'rgba(255,255,255,0.52)' : 'rgba(30, 40, 70, 0.48)'};
          backdrop-filter: blur(18px) saturate(180%);
          -webkit-backdrop-filter: blur(18px) saturate(180%);
          border: 1px solid ${isLight ? 'rgba(99,102,241,0.18)' : 'rgba(148,163,252,0.15)'};
          border-radius: 999px;
          box-shadow: ${isLight
            ? '0 8px 32px rgba(0,0,0,0.13), inset 0 1px 1px rgba(255,255,255,0.7), inset 0 -1px 1px rgba(0,0,0,0.06)'
            : '0 8px 32px rgba(0,0,0,0.45), inset 0 1px 1px rgba(255,255,255,0.07), inset 0 -1px 1px rgba(0,0,0,0.2)'};
          animation: ce-bar-up 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.35s both;
        }
        .ce-bar-inner {
          padding: 10px 20px;
          display: flex; align-items: center; justify-content: center;
          flex-wrap: wrap; gap: 9px;
        }
        .ce-bar-note { font-size: 0.72rem; color: ${C.muted}; margin-right: auto; }
        .ce-btn {
          padding: 9px 22px; border-radius: 7px; font-size: 0.875rem;
          font-weight: 600; cursor: pointer; border: none; font-family: inherit;
          display: inline-flex; align-items: center; min-width: 110px; justify-content: center;
          transition: opacity 0.15s, transform 0.18s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s;
        }
        .ce-btn:active { transform: scale(0.96) !important; }
        .ce-btn-ghost {
          background: transparent; color: ${C.muted}; border: 1px solid ${C.border};
        }
        .ce-btn-ghost:hover { background: ${C.inputBg}; transform: translateY(-1px); }
        .ce-btn-primary {
          background: linear-gradient(135deg, ${C.primary}, ${C.accent});
          color: #fff; box-shadow: 0 3px 12px ${C.primary}30;
        }
        .ce-btn-primary:not(:disabled):hover {
          opacity: 0.9; transform: translateY(-2px);
          box-shadow: 0 8px 24px ${C.primary}45;
        }
        .ce-btn:disabled { opacity: 0.36; cursor: not-allowed; transform: none !important; }

        .ce-btn-draft {
          background: transparent;
          color: ${C.primary};
          border: 1.5px dashed ${C.primary};
        }
        .ce-btn-draft:not(:disabled):hover {
          background: ${C.primary}12;
          transform: translateY(-1px);
          border-style: solid;
        }
        .ce-draft-indicator {
          position: fixed; bottom: calc(20px + 58px + 10px); left: 44%; transform: translateX(-50%);
          display: flex; align-items: center; gap: 8px;
          padding: 7px 18px; border-radius: 999px;
          font-size: 12px; color: ${C.muted};
          background: ${isLight ? 'rgba(255,255,255,0.52)' : 'rgba(30, 40, 70, 0.48)'};
          backdrop-filter: blur(18px) saturate(180%);
          -webkit-backdrop-filter: blur(18px) saturate(180%);
          border: 1px solid ${isLight ? 'rgba(99,102,241,0.18)' : 'rgba(148,163,252,0.15)'};
          box-shadow: ${isLight
            ? '0 4px 16px rgba(0,0,0,0.10)'
            : '0 4px 16px rgba(0,0,0,0.35)'};
          z-index: 499; white-space: nowrap;
          animation: ce-slide-up 0.4s ease forwards;
          transition: all 0.3s ease;
        }
        .ce-draft-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: #f59e0b; flex-shrink: 0;
          animation: ce-draft-pulse 2s ease-in-out infinite;
        }
        .ce-draft-dot-saving {
          background: ${C.primary};
          animation: ce-draft-spin 0.8s linear infinite;
          width: 8px; height: 8px;
          border: 2px solid transparent;
          border-top-color: ${C.primary};
          border-radius: 50%;
          background: transparent;
        }
        .ce-draft-dot-saved {
          background: #10b981;
          animation: none;
          box-shadow: 0 0 6px #10b98166;
        }
        .ce-draft-dot-waiting {
          background: ${C.muted};
          opacity: 0.4;
          animation: ce-draft-pulse 3s ease-in-out infinite;
        }
        @keyframes ce-draft-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes ce-draft-spin {
          to { transform: rotate(360deg); }
        }

        /* ─── RESPONSIVE ─────────────────────────────────────────────── */
        @media (max-width: 960px) {
          .ce-page { padding: 0 16px; }
          .ce-header { padding: 44px 0 22px; }
          .ce-layout { gap: 24px; padding: 0 0 28px; }
          .ce-layout-hero { max-width: 400px; padding-top: 16px; }
          .ce-bar { max-width: calc(100vw - 32px); }
        }
        @media (max-width: 560px) {
          .ce-bar {
            left: 10px;
            right: 10px;
            width: auto;
            max-width: none;
            transform: none;
            bottom: max(12px, env(safe-area-inset-bottom, 0px));
            border-radius: 14px;
            animation: ce-bar-up-mobile 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.35s both;
          }
          .ce-bar-inner {
            flex-direction: column;
            align-items: stretch;
            padding: 10px 12px;
            gap: 8px;
          }
          .ce-bar-inner .ce-btn {
            width: 100%;
            min-width: 0;
            flex: none;
          }
        }
        @media (max-width: 600px) {
          .ce-section { grid-template-columns: 1fr; gap: 10px; padding: 18px 0; }
          .ce-section-desc { display: none; }
          .ce-row, .ce-opts-2 { grid-template-columns: 1fr; }
          .ce-review { grid-template-columns: 1fr; }
          .ce-rv { border-right: none !important; }
          .ce-rv:nth-last-child(-n+2) { border-bottom: 1px solid ${C.divider}; }
          .ce-rv:last-child { border-bottom: none; }
        }
      `}</style>

      <div className="ce-page">
      {/* HEADER */}
      <div className="ce-header">
        <div className="ce-header-eyebrow">{isEdit ? "Edit" : "New event"}</div>
        <h1 className="ce-header-title">{isEdit ? t.updateEvent : t.title}</h1>
        <p className="ce-header-sub">{t.subtitle}</p>
        {!isEdit && recreateFromId && (
          <div className="ce-header-recreate-badge" title={recreateSourceTitle || undefined}>
            ♻️ {lang === "fr" ? "Recréation depuis" : "Recreating from"}: {recreateSourceTitle || `#${recreateFromId}`}
          </div>
        )}
      </div>

      <div className="ce-layout">
        <div className="ce-layout-hero">
          <Collapse show={isFirstFreeEvent}>
            <AnimSection delay={0}>
              <div
                className="ce-first-free-banner"
                style={{
                  marginBottom: "16px", borderRadius: "12px", overflow: "hidden",
                  border: "1px solid rgba(52,211,153,0.28)",
                  background: "linear-gradient(135deg, rgba(16,185,129,0.08) 0%, rgba(52,211,153,0.05) 100%)",
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", padding: "18px 20px" }}>
                  <div style={{
                    flexShrink: 0, width: "38px", height: "38px", borderRadius: "10px",
                    background: "rgba(52,211,153,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.25rem",
                  }}>🎉</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#34d399", marginBottom: "4px" }}>
                      {t.firstEventBadge}
                    </div>
                    <div style={{ fontSize: "0.92rem", fontWeight: 600, color: C.fg, marginBottom: "6px", lineHeight: 1.3 }}>
                      {t.firstEventTitle}
                    </div>
                    <p style={{ fontSize: "0.78rem", color: C.muted, lineHeight: 1.6, margin: 0 }}>
                      {t.firstEventDesc}
                    </p>
                  </div>
                </div>
              </div>
            </AnimSection>
          </Collapse>
          <div className="ce-prev-eyebrow">
            <span className="ce-prev-dot" />
            {t.preview}
          </div>
          <EventPreviewCard formData={formData} coverPreview={coverPreview} themeVars={C} t={t} lang={lang} />
          <p className="ce-prev-sub">{t.previewHint}</p>
        </div>

        <div className="ce-form-col">
          <form onSubmit={handleSubmit} className="ce-form">

            {/* 01 */}
            <AnimSection delay={40}>
            <div
              ref={coverSectionRef}
              className={`ce-section${recreateCoverAttention ? " ce-cover-attention" : ""}`}
            >
              <div>
                <div className="ce-section-num">01</div>
                <div className="ce-section-title">{t.basicInfo}</div>
                <p className="ce-section-desc">Name, date and time.</p>
              </div>
              <div className="ce-section-body">
                <div className="ce-group">
                  <label className="ce-label">{t.eventName} <span className="ce-req">*</span></label>
                  <input type="text" name="title" value={formData.title} onChange={handleChange}
                    placeholder={t.eventNamePlaceholder} className="ce-input" required />
                </div>
                <div className="ce-row">
                  <div className="ce-group">
                    <label className="ce-label">{t.date} <span className="ce-req">*</span></label>
                    <input type="date" name="date" value={formData.date} onChange={handleChange} className="ce-input" required />
                  </div>
                  <div className="ce-group">
                    <label className="ce-label">{t.time}</label>
                    <input type="time" name="time" value={formData.time} onChange={handleChange} className="ce-input" defaultValue="19:00" />
                  </div>
                </div>
              </div>
            </div>

            {/* 02 */}
            </AnimSection>
            <AnimSection delay={80}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">02</div>
                <div className="ce-section-title">{t.location}</div>
                <p className="ce-section-desc">Search or pin on map.</p>
              </div>
              <div className="ce-section-body">
                <div className="ce-group">
                  <label className="ce-label">{t.location} <span className="ce-req">*</span></label>
                  <div className="ce-loc-wrap">
                    <input ref={locationInputRef} name="location" value={formData.location}
                      onChange={handleLocationChange}
                      onFocus={() => !isAddressSelected && suggestions.length > 0 && setShowSuggestions(true)}
                      placeholder={t.locationPlaceholder}
                      autoComplete="off" autoCorrect="off" spellCheck={false}
                      className={`ce-input${showSuggestions ? ' open' : ''}`} />
                    {showSuggestions && (
                      <div ref={suggestionsRef} className="ce-sugg">
                        {isSearching ? (
                          <div className="ce-sugg-state">{t.searching}</div>
                        ) : suggestions.length > 0 ? suggestions.map((s, i) => (
                          <div
                            key={i}
                            className="ce-sugg-item"
                            onMouseDown={(e) => { e.preventDefault(); handleAddressSelect(s); }}
                          >
                            <div className="ce-sugg-main">{s.display_name}</div>
                            {s.address && <div className="ce-sugg-sub">{s.address.road && `${s.address.road}, `}{s.address.city || s.address.town || s.address.village || ""}</div>}
                          </div>
                        )) : <div className="ce-sugg-state">{t.noResults}</div>}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <button type="button" className="ce-map-btn" onClick={() => setShowMap(!showMap)}>
                    <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6-10l6-3m0 13l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 10m0 3V7"/>
                    </svg>
                    {showMap ? (lang === "fr" ? "Masquer" : "Hide map") : (lang === "fr" ? "Afficher la carte" : "Show map")}
                  </button>
                </div>
                <Collapse show={showMap}>
                  <div className="ce-map-box">
                    <Map ref={mapRef}
                      initialViewState={{ longitude: position?.longitude || (isGuruDomain ? 77.2090 : 2.3522), latitude: position?.latitude || (isGuruDomain ? 28.6139 : 48.8566), zoom: position ? 16 : 10 }}
                      mapStyle={`https://basemaps.cartocdn.com/gl/${isLight ? "positron" : "dark-matter"}-gl-style/style.json`}
                      onClick={handleMapClick} style={{ width: "100%", height: "100%" }}>
                      {position && (
                        <Marker longitude={position.longitude} latitude={position.latitude}
                          color={C.primary} draggable
                          onDragEnd={(e) => {
                            const p = { longitude: e.lngLat.lng, latitude: e.lngLat.lat };
                            setPosition(p); setIsAddressSelected(false); fetchAddressFromCoordinates(p);
                          }} />
                      )}
                      <NavigationControl position="top-right" />
                    </Map>
                  </div>
                </Collapse>
              </div>
            </div>

            {/* 03 */}
            </AnimSection>
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">03</div>
                <div className="ce-section-title">{t.eventCover}</div>
                <p className="ce-section-desc">PNG, JPG, WEBP · max 5MB</p>
              </div>
              <div className="ce-section-body">
                <div className="ce-cover" onClick={() => coverInputRef.current?.click()}>
                  {coverPreview ? (
                    <>
                      <img src={coverPreview} alt="" className="ce-cover-img" />
                      <div style={{ position: 'absolute', bottom: 12, right: 12, display: 'flex', gap: 8, zIndex: 2 }}>
                        {originalCoverUrl && (
                          <button type="button" className="ce-cover-action"
                            onClick={(e) => { e.stopPropagation(); handleRecrop(); }}>
                            {t.recropCover}
                          </button>
                        )}
                        <button type="button" className="ce-cover-action"
                          onClick={(e) => { e.stopPropagation(); coverInputRef.current?.click(); }}>
                          {t.changeCover}
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="ce-cover-empty">
                      <svg width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{opacity:0.3}}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21zm16.5-12a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                      </svg>
                      <div className="ce-cover-lbl">{t.dropImage}</div>
                      <div className="ce-cover-sub">16:9 · max 5MB</div>
                    </div>
                  )}
                  <input type="file" ref={coverInputRef} className="ce-cover-input"
                    accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleCoverUpload} />
                </div>
                {coverError && <p className="ce-err">{coverError}</p>}
                {!isEdit && recreateFromId && isRecreateCoverLoading && (
                  <p className="ce-err">
                    {lang === "fr"
                      ? "Préparation automatique de la couverture en cours..."
                      : "Preparing cover automatically..."}
                  </p>
                )}
                {!isEdit && recreateFromId && !isRecreateCoverLoading && recreateCoverNeedsUpload && !coverFile && (
                  <p className="ce-err">
                    {lang === "fr"
                      ? "La couverture n'a pas pu être copiée automatiquement. Veuillez la re-téléverser avant publication."
                      : "Cover could not be copied automatically. Please re-upload it before publish."}
                  </p>
                )}
              </div>
            </div>

            {/* 04 */}
            </AnimSection>
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">04</div>
                <div className="ce-section-title">{t.description}</div>
                <p className="ce-section-desc">Agenda, speakers, details.</p>
              </div>
              <div className="ce-section-body">
                <div className="ce-group">
                  <label className="ce-label">{t.description} <span className="ce-req">*</span></label>
                  <textarea name="description" value={formData.description} onChange={handleChange}
                    placeholder={t.descriptionPlaceholder} className="ce-textarea" required />
                </div>
              </div>
            </div>

            {/* 05 */}
            </AnimSection>
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">05</div>
                <div className="ce-section-title">{t.organizer}</div>
                <p className="ce-section-desc">Contact info for attendees.</p>
              </div>
              <div className="ce-section-body">
                <div className="ce-group">
                  <label className="ce-label">{t.organizerName} <span className="ce-req">*</span></label>
                  <input type="text" name="organizer" value={formData.organizer} onChange={handleChange}
                    placeholder={t.organizerNamePlaceholder} className="ce-input" required />
                </div>
                <div className="ce-row">
                  <div className="ce-group">
                    <label className="ce-label">{t.email} <span className="ce-req">*</span></label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange}
                      placeholder={t.emailPlaceholder} className="ce-input" required />
                  </div>
                  <div className="ce-group">
                    <label className="ce-label">{t.phone}</label>
                    <div className="ce-phone">
                      <span className="ce-phone-code-static" aria-hidden="true">+33</span>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        onPaste={handlePhonePaste}
                        inputMode="tel"
                        autoComplete="tel-national"
                        pattern="[0-9]*"
                        placeholder={t.phonePlaceholder}
                        className="ce-input"
                        style={{ flex: 1 }}
                        maxLength="9"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 06.1 — PRICING MODEL EXPLANATION */}
            </AnimSection>
            <AnimSection delay={0}>
            <div style={{
              borderRadius: "12px", border: `1px solid ${C.border}`,
              background: C.inputBg, overflow: "hidden", marginBottom: "4px",
              marginTop: "10px",
            }}>
              <div style={{ padding: "12px 18px", borderBottom: `1px solid ${C.divider}`, display: "flex", alignItems: "center", gap: "8px" }}>
                <svg width="14" height="14" fill="none" stroke={C.primary} strokeWidth="2" viewBox="0 0 24 24" style={{flexShrink:0}}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <span style={{ fontSize: "0.75rem", fontWeight: 700, color: C.primary, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                  {t.pricingExplainTitle}
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
                <div style={{ padding: "16px 18px", borderRight: `1px solid ${C.divider}` }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#34d399", marginBottom: "7px" }}>{t.pricingExplainFreeTitle}</div>
                  <p style={{ fontSize: "0.76rem", color: C.muted, lineHeight: 1.6, margin: 0 }}>{t.pricingExplainFreeDesc}</p>
                </div>
                <div style={{ padding: "16px 18px" }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#a78bfa", marginBottom: "7px" }}>{t.pricingExplainPaidTitle}</div>
                  <p style={{ fontSize: "0.76rem", color: C.muted, lineHeight: 1.6, margin: 0 }}>{t.pricingExplainPaidDesc}</p>
                </div>
              </div>
            </div>

            {/* 06 */}
            </AnimSection>
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">06</div>
                <div className="ce-section-title">{t.tickets}</div>
                <p className="ce-section-desc">Free or paid admission.</p>
              </div>
              <div className="ce-section-body">
                <label className={`ce-option${formData.isFree ? ' on' : ''}`}>
                  <input type="checkbox" checked={formData.isFree}
                    onChange={(e) => handleChange({ target: { name: "isFree", type: "checkbox", checked: e.target.checked } })} />
                  <div><div className="ce-opt-title">{t.isFree}</div></div>
                </label>
                <Collapse show={!formData.isFree}>
                  <div className="ce-row">
                    <div className="ce-group">
                      <label className="ce-label">{t.ticketPrice}</label>
                      <input type="number" name="ticketPrice" value={formData.ticketPrice} onChange={handleChange}
                        placeholder="0.00" min="0" max="9999999999.99" step="0.01" className="ce-input" />
                    </div>
                    <div className="ce-group">
                      <label className="ce-label">{t.currency}</label>
                      <select name="currency" value={formData.currency} onChange={handleChange} className="ce-select">
                        {isGuruDomain ? (
                          <option value="INR">INR (₹)</option>
                        ) : (
                          <>
                            <option value="EUR">EUR (€)</option>
                            <option value="USD">USD ($)</option>
                            <option value="GBP">GBP (£)</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                </Collapse>
              </div>
            </div>

            {/* 07 */}
            </AnimSection>
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">07</div>
                <div className="ce-section-title">{t.visibility}</div>
                <p className="ce-section-desc">Who can find and join.</p>
              </div>
              <div className="ce-section-body">
                <div className="ce-opts-2">
                  <label className={`ce-option${formData.isPrivate ? ' on' : ''}`}>
                    <input type="radio" checked={formData.isPrivate} onChange={() => setFormData(p => ({...p, isPrivate: true}))} />
                    <div><div className="ce-opt-title">{t.isPrivate}</div><p className="ce-opt-desc">{t.isPrivateDesc}</p></div>
                  </label>
                  <label className={`ce-option${!formData.isPrivate ? ' on' : ''}`}>
                    <input type="radio" checked={!formData.isPrivate} onChange={() => setFormData(p => ({...p, isPrivate: false}))} />
                    <div><div className="ce-opt-title">{t.isPublic}</div><p className="ce-opt-desc">{t.isPublicDesc}</p></div>
                  </label>
                </div>
                <label className={`ce-option${formData.allowUserInvitations ? ' on' : ''}`}>
                  <input type="checkbox" checked={formData.allowUserInvitations}
                    onChange={(e) => handleChange({ target: { name: "allowUserInvitations", type: "checkbox", checked: e.target.checked } })} />
                  <div><div className="ce-opt-title">{t.allowUserInvitations}</div><p className="ce-opt-desc">{t.allowUserInvitationsDesc}</p></div>
                </label>
              </div>
            </div>

            {/* 08 */}
            </AnimSection>
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">08</div>
                <div className="ce-section-title">{t.matching}</div>
                <p className="ce-section-desc">Connect by interest.</p>
              </div>
              <div className="ce-section-body">
                <label className={`ce-option${formData.allowExternalMatching ? ' on' : ''}`}>
                  <input type="checkbox" checked={formData.allowExternalMatching}
                    onChange={(e) => handleChange({ target: { name: "allowExternalMatching", type: "checkbox", checked: e.target.checked } })} />
                  <div><div className="ce-opt-title">{t.allowExternalMatching}</div><p className="ce-opt-desc">{t.allowExternalMatchingDesc}</p></div>
                </label>
                <Collapse show={formData.allowExternalMatching}>
                  <div className="ce-row" style={{ paddingTop: "4px" }}>
                    <div className="ce-group">
                      <label className="ce-label">{t.matchingStartBefore}</label>
                      <input type="date" name="matchingStartBefore" value={formData.matchingStartBefore} onChange={handleChange} className="ce-input" />
                    </div>
                    <div className="ce-group">
                      <label className="ce-label">{t.matchingStartAfter}</label>
                      <input type="date" name="matchingStartAfter" value={formData.matchingStartAfter} onChange={handleChange} className="ce-input" />
                    </div>
                  </div>
                </Collapse>
              </div>
            </div>

            {/* 09 */}
            </AnimSection>
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">09</div>
                <div className="ce-section-title">{t.participantSettings}</div>
                <p className="ce-section-desc">Access requirements.</p>
              </div>
              <div className="ce-section-body">
                <label className={`ce-option${formData.requireParticipantPhone ? ' on' : ''}`}>
                  <input type="checkbox" checked={formData.requireParticipantPhone}
                    onChange={(e) => handleChange({ target: { name: "requireParticipantPhone", type: "checkbox", checked: e.target.checked } })} />
                  <div><div className="ce-opt-title">{t.requireParticipantPhone}</div><p className="ce-opt-desc">{t.requireParticipantPhoneDesc}</p></div>
                </label>
              </div>
            </div>

            {/* 10 — CAPACITY & COST */}
            </AnimSection>
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">10</div>
                <div className="ce-section-title">{t.capacitySection}</div>
                <p className="ce-section-desc">{t.capacitySectionDesc}</p>
              </div>
              <div className="ce-section-body">
                <div style={{
                  borderRadius: "18px", border: "1px solid rgba(99,102,241,0.22)",
                  background: "linear-gradient(145deg, rgba(99,102,241,0.06) 0%, rgba(139,92,246,0.03) 100%)",
                  overflow: "hidden",
                }}>
                  <div style={{
                    padding: "1rem 1.25rem", display: "flex", alignItems: "center",
                    gap: "0.75rem", flexWrap: "wrap", borderBottom: "1px solid rgba(99,102,241,0.1)",
                    background: "rgba(99,102,241,0.04)",
                  }}>
                    <span style={{ fontSize: "1.05rem" }}>👥</span>
                    <label className="ce-label" style={{ margin: 0, fontSize: "0.88rem", opacity: 0.75, flexShrink: 0 }}>
                      {t.capacity}
                    </label>
                    <input type="number" name="capacity" value={formData.capacity} onChange={handleChange}
                      placeholder={t.capacityPlaceholder} min="1" className="ce-input"
                      style={{ maxWidth: "120px", padding: "0.4rem 0.75rem", fontSize: "0.95rem", borderRadius: "10px", height: "36px" }}
                    />
                    <span style={{
                      marginLeft: "auto", fontSize: "0.72rem", fontWeight: 600,
                      color: "rgba(139,92,246,0.8)", background: "rgba(139,92,246,0.1)",
                      border: "1px solid rgba(139,92,246,0.2)", padding: "3px 10px",
                      borderRadius: "20px", letterSpacing: "0.3px", whiteSpace: "nowrap",
                    }}>
                      💶 {t.estimatedCostNote}
                    </span>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 0 }}>
                    {/* Participants */}
                    <div style={{ padding: "1.25rem 1.5rem" }}>
                      <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", opacity: 0.45, marginBottom: "6px" }}>
                        {lang === "fr" ? "Participants" : "Participants"}
                      </div>
                      <div style={{ fontSize: "2.2rem", fontWeight: 800, lineHeight: 1, color: "var(--fg, #e6e9f0)", fontVariantNumeric: "tabular-nums" }}>
                        {formData.capacity && parseInt(formData.capacity) > 0
                          ? parseInt(formData.capacity).toLocaleString()
                          : <span style={{ opacity: 0.25, fontSize: "1.4rem" }}>—</span>}
                      </div>
                      <div style={{ fontSize: "0.75rem", opacity: 0.35, marginTop: "4px" }}>
                        {lang === "fr" ? "places max." : "max. seats"}
                      </div>
                    </div>

                    <div style={{ width: "1px", background: "rgba(99,102,241,0.12)", margin: "1rem 0" }} />

                    {/* Total cost */}
                    <div style={{ padding: "1.25rem 1.5rem" }}>
                      <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", opacity: 0.45, marginBottom: "6px" }}>
                        {isFirstFreeEvent ? (lang === "fr" ? "Statut" : "Status") : (lang === "fr" ? "Coût total" : "Total Cost")}
                      </div>
                      {isFirstFreeEvent ? (
                        <>
                          <div style={{ fontSize: "1.3rem", fontWeight: 800, lineHeight: 1, color: "#34d399", fontVariantNumeric: "tabular-nums" }}>
                            {lang === "fr" ? "Gratuit 🎉" : "Free 🎉"}
                          </div>
                          <div style={{ fontSize: "0.72rem", color: "#34d399", opacity: 0.8, marginTop: "4px" }}>
                            {t.firstEventCapacityNote}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={{ fontSize: "2.2rem", fontWeight: 800, lineHeight: 1, color: formData.capacity && parseInt(formData.capacity) > 0 ? "#8b5cf6" : "var(--fg, #e6e9f0)", fontVariantNumeric: "tabular-nums" }}>
                            {formData.capacity && parseInt(formData.capacity) > 0
                              ? `€${(parseInt(formData.capacity) * 2).toLocaleString()}`
                              : <span style={{ opacity: 0.25, fontSize: "1.4rem" }}>—</span>}
                          </div>
                          <div style={{ fontSize: "0.75rem", opacity: 0.35, marginTop: "4px" }}>
                            {formData.capacity && parseInt(formData.capacity) > 0
                              ? `${parseInt(formData.capacity).toLocaleString()} × €2`
                              : lang === "fr" ? "entrez le nombre ci-dessus" : "enter a number above"}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Progress bar footer */}
                  <div style={{ padding: "0 1.25rem 1rem", borderTop: "1px solid rgba(99,102,241,0.08)" }}>
                    <div style={{ height: "3px", borderRadius: "2px", background: "rgba(99,102,241,0.1)", overflow: "hidden", marginTop: "0.85rem" }}>
                      <div style={{
                        height: "100%",
                        width: formData.capacity && parseInt(formData.capacity) > 0 ? `${Math.min(parseInt(formData.capacity) / 1000 * 100, 100)}%` : "0%",
                        borderRadius: "2px",
                        background: "linear-gradient(90deg, #6366f1, #a78bfa)",
                        transition: "width 0.35s cubic-bezier(.4,0,.2,1)",
                      }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 10.1 — TABLES & SEATING */}
            </AnimSection>
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">10.1</div>
                <div className="ce-section-title">{t.tables}</div>
                <p className="ce-section-desc">{t.tablesDesc}</p>
              </div>
              <div className="ce-section-body">
                {/* Toggle */}
                <label className={`ce-option${formData.hasTables ? ' on' : ''}`}>
                  <input type="checkbox" checked={formData.hasTables}
                    onChange={(e) => handleChange({ target: { name: "hasTables", type: "checkbox", checked: e.target.checked } })} />
                  <div>
                    <div className="ce-opt-title">{t.hasTables}</div>
                    <p className="ce-opt-desc">{t.hasTablesDesc}</p>
                  </div>
                </label>

                <Collapse show={formData.hasTables}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "12px", paddingTop: "4px" }}>                    {/* Two explicit fields side by side */}
                    <div className="ce-row">
                      {/* Number of tables */}
                      <div className="ce-group">
                        <label className="ce-label">{t.numberOfTables} <span className="ce-req">*</span></label>
                        <input
                          type="number" name="numberOfTables"
                          value={formData.numberOfTables} onChange={handleChange}
                          placeholder={t.numberOfTablesPlaceholder}
                          min="1" className="ce-input"
                        />
                      </div>

                      {/* Seats per table */}
                      <div className="ce-group">
                        <label className="ce-label">{t.seatsPerTable} <span className="ce-req">*</span></label>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <input
                            type="number" name="seatsPerTable"
                            value={formData.seatsPerTable} onChange={handleChange}
                            placeholder={t.seatsPerTablePlaceholder}
                            min="2" className="ce-input"
                          />
                          <span style={{ fontSize: "0.72rem", color: C.muted, whiteSpace: "nowrap", flexShrink: 0 }}>
                            {t.seatsPerTableNote}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Summary card — shown when both fields are valid */}
                    <Collapse show={tablesCount !== null && totalTableSeats !== null}>
                      <div style={{
                        display: "grid", gridTemplateColumns: "1fr 1px 1fr 1px 1fr",
                        borderRadius: "12px", overflow: "hidden",
                        border: "1px solid rgba(251,191,36,0.25)",
                        background: "rgba(251,191,36,0.05)",
                        marginTop: "4px",
                      }}>
                        {/* Tables */}
                        <div style={{ padding: "14px 16px", textAlign: "center" }}>
                          <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#f59e0b", opacity: 0.7, marginBottom: "4px" }}>
                            {lang === "fr" ? "Tables" : "Tables"}
                          </div>
                          <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#f59e0b", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                            {tablesCount ?? '—'}
                          </div>
                        </div>
                        <div style={{ background: "rgba(251,191,36,0.15)" }} />
                        {/* Seats per table */}
                        <div style={{ padding: "14px 16px", textAlign: "center" }}>
                          <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#f59e0b", opacity: 0.7, marginBottom: "4px" }}>
                            {lang === "fr" ? "Places/table" : "Seats/table"}
                          </div>
                          <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#f59e0b", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                            {formData.seatsPerTable}
                          </div>
                        </div>
                        <div style={{ background: "rgba(251,191,36,0.15)" }} />
                        {/* Total seats */}
                        <div style={{ padding: "14px 16px", textAlign: "center" }}>
                          <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#f59e0b", opacity: 0.7, marginBottom: "4px" }}>
                            {t.totalSeatsLabel}
                          </div>
                          <div style={{ fontSize: "1.8rem", fontWeight: 800, color: "#f59e0b", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                            {totalTableSeats != null ? totalTableSeats.toLocaleString() : '—'}
                          </div>
                        </div>
                      </div>
                    </Collapse>

                    {/* Explanation note */}
                    <div style={{
                      display: "flex", alignItems: "flex-start", gap: "10px",
                      fontSize: "0.76rem", color: C.muted, lineHeight: 1.6,
                      padding: "12px 14px", borderRadius: "8px",
                      background: C.inputBg, border: `1px solid ${C.border}`,
                    }}>
                      <span style={{ fontSize: "1rem", flexShrink: 0, marginTop: "1px" }}>💡</span>
                      <span>{t.tablesExplain}</span>
                    </div>
                  </div>
                </Collapse>
              </div>
            </div>

            {/* 10.2 — TABLE TIMERS */}
            </AnimSection>
            {formData.hasTables && (
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">10.2</div>
                <div className="ce-section-title">{t.tableTimers}</div>
                <p className="ce-section-desc">{t.tableTimersDesc}</p>
              </div>
              <div className="ce-section-body">
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

                  {/* Timer 1 — Time at table */}
                  <div style={{
                    background: C.inputBg, border: `1px solid ${C.border}`,
                    borderRadius: "14px", padding: "18px 20px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                          <span style={{ fontSize: "1.1rem" }}>⏱️</span>
                          <span style={{ fontWeight: 700, fontSize: "0.92rem", color: C.fg }}>{t.tableTimerSeatLabel}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: "0.75rem", color: C.muted, lineHeight: 1.5 }}>{t.tableTimerSeatDesc}</p>
                      </div>
                      <div style={{
                        minWidth: "62px", textAlign: "center",
                        background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.28)",
                        borderRadius: "10px", padding: "8px 12px",
                      }}>
                        <div style={{ fontSize: "1.5rem", fontWeight: 800, color: C.primary, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                          {formData.tableTimerSeat ?? "—"}
                        </div>
                        <div style={{ fontSize: "0.65rem", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: "2px" }}>
                          {t.timerMinutes}
                        </div>
                      </div>
                    </div>

                    {/* Slider */}
                    <div style={{ position: "relative", padding: "4px 0 2px" }}>
                      <input
                        type="range" name="tableTimerSeat"
                        min={1} max={30} step={1}
                        value={formData.tableTimerSeat ?? 10}
                        onChange={handleChange}
                        style={{
                          width: "100%", height: "6px", appearance: "none", WebkitAppearance: "none",
                          background: `linear-gradient(to right, #6366f1 0%, #6366f1 ${(((formData.tableTimerSeat ?? 10) - 1) / 29) * 100}%, ${C.border} ${(((formData.tableTimerSeat ?? 10) - 1) / 29) * 100}%, ${C.border} 100%)`,
                          borderRadius: "4px", outline: "none", cursor: "pointer",
                        }}
                      />
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", fontSize: "0.68rem", color: C.muted }}>
                        <span>1 {t.timerMinutes}</span>
                        <span style={{ color: C.primary, fontWeight: 700 }}>{formData.tableTimerSeat} {t.timerMinutes}</span>
                        <span>30 {t.timerMinutes}</span>
                      </div>
                    </div>

                    {/* Quick presets */}
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "12px" }}>
                      {[3, 5, 7, 10, 15, 20].map(v => (
                        <button key={v} type="button"
                          onClick={() => handleChange({ target: { name: "tableTimerSeat", value: v, type: "number" } })}
                          style={{
                            padding: "4px 10px", borderRadius: "8px", fontSize: "0.75rem", fontWeight: 600,
                            border: `1px solid ${formData.tableTimerSeat === v ? C.primary : C.border}`,
                            background: formData.tableTimerSeat === v ? "rgba(99,102,241,0.14)" : "transparent",
                            color: formData.tableTimerSeat === v ? C.primary : C.muted,
                            cursor: "pointer", transition: "all 0.18s",
                          }}>
                          {v} {t.timerMinutes}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Timer 2 — Table rotation */}
                  <div style={{
                    background: C.inputBg, border: `1px solid ${C.border}`,
                    borderRadius: "14px", padding: "18px 20px",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "3px" }}>
                          <span style={{ fontSize: "1.1rem" }}>🔄</span>
                          <span style={{ fontWeight: 700, fontSize: "0.92rem", color: C.fg }}>{t.tableTimerRotationLabel}</span>
                        </div>
                        <p style={{ margin: 0, fontSize: "0.75rem", color: C.muted, lineHeight: 1.5 }}>{t.tableTimerRotationDesc}</p>
                      </div>
                      <div style={{
                        minWidth: "62px", textAlign: "center",
                        background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)",
                        borderRadius: "10px", padding: "8px 12px",
                      }}>
                        <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "#f59e0b", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                          {formData.tableTimerRotation}
                        </div>
                        <div style={{ fontSize: "0.65rem", fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: "2px" }}>
                          {t.timerMinutes}
                        </div>
                      </div>
                    </div>

                    {/* Slider */}
                    <div style={{ position: "relative", padding: "4px 0 2px" }}>
                      <input
                        type="range" name="tableTimerRotation"
                        min={1} max={15} step={1}
                        value={formData.tableTimerRotation}
                        onChange={handleChange}
                        style={{
                          width: "100%", height: "6px", appearance: "none", WebkitAppearance: "none",
                          background: `linear-gradient(to right, #f59e0b 0%, #f59e0b ${((formData.tableTimerRotation - 1) / 14) * 100}%, ${C.border} ${((formData.tableTimerRotation - 1) / 14) * 100}%, ${C.border} 100%)`,
                          borderRadius: "4px", outline: "none", cursor: "pointer",
                        }}
                      />
                      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", fontSize: "0.68rem", color: C.muted }}>
                        <span>1 {t.timerMinutes}</span>
                        <span style={{ color: "#f59e0b", fontWeight: 700 }}>{formData.tableTimerRotation} {t.timerMinutes}</span>
                        <span>15 {t.timerMinutes}</span>
                      </div>
                    </div>

                    {/* Quick presets */}
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "12px" }}>
                      {[1, 2, 3, 5, 7, 10].map(v => (
                        <button key={v} type="button"
                          onClick={() => handleChange({ target: { name: "tableTimerRotation", value: v, type: "number" } })}
                          style={{
                            padding: "4px 10px", borderRadius: "8px", fontSize: "0.75rem", fontWeight: 600,
                            border: `1px solid ${formData.tableTimerRotation === v ? "#f59e0b" : C.border}`,
                            background: formData.tableTimerRotation === v ? "rgba(251,191,36,0.12)" : "transparent",
                            color: formData.tableTimerRotation === v ? "#f59e0b" : C.muted,
                            cursor: "pointer", transition: "all 0.18s",
                          }}>
                          {v} {t.timerMinutes}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Summary info note */}
                  <div style={{
                    display: "flex", alignItems: "flex-start", gap: "10px",
                    fontSize: "0.76rem", color: C.muted, lineHeight: 1.6,
                    padding: "12px 14px", borderRadius: "8px",
                    background: C.inputBg, border: `1px solid ${C.border}`,
                  }}>
                    <span style={{ fontSize: "1rem", flexShrink: 0, marginTop: "1px" }}>💡</span>
                    <span>
                      {lang === "fr"
                        ? `Cycle complet : ${formData.tableTimerSeat} min à table + ${formData.tableTimerRotation} min de transition = ${formData.tableTimerSeat + formData.tableTimerRotation} min par tour.`
                        : `Full cycle: ${formData.tableTimerSeat} min at table + ${formData.tableTimerRotation} min rotation = ${formData.tableTimerSeat + formData.tableTimerRotation} min per round.`}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            </AnimSection>
            )}

            {/* 11 REVIEW */}
            <AnimSection delay={0}>
            <div className="ce-section">
              <div>
                <div className="ce-section-num">11</div>
                <div className="ce-section-title">{t.reviewTitle}</div>
                <p className="ce-section-desc">Before you submit.</p>
              </div>
              <div className="ce-section-body">
                <div className="ce-review">
                  <div className="ce-rv"><div className="ce-rv-lbl">{t.eventName}</div><div className="ce-rv-val">{formData.title || '—'}</div></div>
                  <div className="ce-rv">
                    <div className="ce-rv-lbl">{t.date}</div>
                    <div className="ce-rv-val">{formData.date ? new Date(formData.date).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</div>
                  </div>
                  <div className="ce-rv"><div className="ce-rv-lbl">{t.time}</div><div className="ce-rv-val">{formData.time || '—'}</div></div>
                  <div className="ce-rv"><div className="ce-rv-lbl">{t.visibility}</div><div className="ce-rv-val">{formData.isPrivate ? t.isPrivate : t.isPublic}</div></div>
                  <div className="ce-rv full"><div className="ce-rv-lbl">{t.location}</div><div className="ce-rv-val">{formData.location || '—'}</div></div>
                  <div className="ce-rv"><div className="ce-rv-lbl">{t.tickets}</div><div className="ce-rv-val">{formData.isFree ? t.free : `${formData.ticketPrice || '0'} ${formData.currency}`}</div></div>
                  <div className="ce-rv"><div className="ce-rv-lbl">{t.tables}</div><div className="ce-rv-val">{formData.hasTables && formData.numberOfTables && formData.seatsPerTable ? `${formData.numberOfTables} × ${formData.seatsPerTable}` : formData.hasTables ? '✓' : '—'}</div></div>
                </div>
                <p className="ce-review-hint">{t.reviewHint}</p>
                <p style={{ fontSize: '0.78rem', color: '#f87171', fontWeight: 700, margin: '6px 0 0', letterSpacing: '0.04em' }}>{t.requiredFieldsNote}</p>

                {validationIssues.length > 0 && (
                  <div className="ce-issues" role="alert" aria-live="polite">
                    <div className="ce-issues-title">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      {t.cantCreateYet}
                    </div>
                    <ul className="ce-issues-list">
                      {validationIssues.map((label, i) => (
                        <li key={i}>{label}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {message && <div className={`ce-banner${status === "success" ? ' ok' : ' err'}`}>{message}</div>}
            </AnimSection>
          </form>
        </div>
      </div>
      </div>

      {/* IMAGE CROP MODAL */}
      {cropImageUrl && (
        <ImageCropModal
          src={cropImageUrl}
          fileName={pendingCropName}
          fileType={pendingCropType}
          isLight={isLight}
          t={t}
          onCancel={handleCropCancel}
          onApply={handleCropApply}
        />
      )}

      {/* STICKY ACTION BAR */}
      <div className="ce-bar">
        <div className="ce-bar-inner">
          <button type="button" className="ce-btn ce-btn-ghost" onClick={handleCancel}>{t.cancel}</button>
          {!isEdit && (
            <button
              type="button"
              className="ce-btn ce-btn-draft"
              onClick={handleSaveDraft}
              disabled={savingDraft}
            >
              {savingDraft ? t.savingDraft : t.saveDraft}
            </button>
          )}
          <button
            type="submit"
            className={`ce-btn ce-btn-primary${recreateCoverAttention ? " ce-attention" : ""}`}
            onClick={handleSubmit}
            disabled={isSubmitting || isDisabled}
          >
            {isSubmitting ? (isEdit ? t.updating : t.loading) : (isEdit ? t.updateEvent : t.createEvent)}
          </button>
        </div>
      </div>
    </div>
  );
}